type CivicPageKind = 'cause' | 'principle' | 'bill' | 'official' | 'action-packet' | 'issue' | 'evidence-packet' | 'source-document' | 'record-document' | 'explanation' | 'risk-assessment' | 'causes' | 'principles' | 'issues' | 'officials' | 'evidence-packets';
type CivicTargetType = 'cause' | 'principle' | 'position-statement';
type VerificationLevel = 'unverified' | 'verified' | 'residency-verified' | 'identity-verified';
type VisibilityMode = 'private' | 'first-name' | 'full-name';

interface BillIssueLinkDto {
  issueId: string;
  issueName: string;
  linkType: string;
  confidence: number;
  rationale?: string | null;
}

interface BillPrincipleImpactDto {
  principleId: string;
  principleName: string;
  impactDirection: string;
  confidence: number;
  rationale?: string | null;
}

interface BillPositionStatementDto {
  positionStatementId: string;
  title: string;
  summary: string;
  positionType: string;
  statementGroupId: string;
  isSignable: boolean;
  causeId?: string | null;
  principleId?: string | null;
  versionNumber: number;
  createdUtc: string;
}

interface BillEvidencePacketDto {
  evidencePacketId: string;
  title: string;
  summary?: string | null;
}

interface BillCivicDetailDto {
  id: string;
  billCode: string;
  title: string;
  summary?: string | null;
  jurisdictionId: string;
  issues: BillIssueLinkDto[];
  principles: BillPrincipleImpactDto[];
  positionStatements: BillPositionStatementDto[];
  evidencePackets: BillEvidencePacketDto[];
}

interface CausePrincipleDto {
  principleId: string;
  name: string;
  summary?: string | null;
}

interface CauseIssueDto {
  issueId: string;
  name: string;
  summary?: string | null;
}

interface IssueListItemDto {
  id: string;
  slug: string;
  name: string;
  summary?: string | null;
  billCount: number;
  causeCount: number;
  principleCount: number;
  actionPacketCount: number;
}

interface IssueBillSummaryDto {
  billId: string;
  billCode: string;
  title: string;
  summary?: string | null;
  jurisdictionId: string;
  linkType: string;
  confidence: number;
  rationale?: string | null;
}

interface IssueDetailDto {
  id: string;
  slug: string;
  name: string;
  summary?: string | null;
  principles: CausePrincipleDto[];
  causes: CauseSummaryDto[];
  bills: IssueBillSummaryDto[];
  actionPackets: ActionPacketSummaryDto[];
}

interface PositionStatementSummaryDto {
  id: string;
  title: string;
  summary: string;
  positionType: string;
  statementGroupId: string;
  isSignable: boolean;
  billId?: string | null;
  causeId?: string | null;
  principleId?: string | null;
  versionNumber: number;
  createdUtc: string;
}

interface EvidencePacketSummaryDto {
  id: string;
  title: string;
  summary?: string | null;
  items: string[];
}

interface EvidencePacketReferenceDto {
  id: string;
  title: string;
  summary?: string | null;
}

interface EvidencePacketListItemDto {
  id: string;
  title: string;
  summary?: string | null;
  itemCount: number;
  billId?: string | null;
  billCode?: string | null;
  causeId?: string | null;
  causeName?: string | null;
  positionStatementId?: string | null;
  positionStatementTitle?: string | null;
  actionPacketCount: number;
}

interface CauseDetailDto {
  id: string;
  slug: string;
  name: string;
  summary?: string | null;
  description?: string | null;
  isActive: boolean;
  principles: CausePrincipleDto[];
  issues: CauseIssueDto[];
  positionStatements: PositionStatementSummaryDto[];
  evidencePackets: EvidencePacketSummaryDto[];
}

interface CauseSummaryDto {
  id: string;
  name: string;
  summary?: string | null;
}

interface CauseListItemDto {
  id: string;
  slug: string;
  name: string;
  summary?: string | null;
  isActive: boolean;
  principleCount: number;
  issueCount: number;
  positionStatementCount: number;
  actionPacketCount: number;
}

interface PrincipleBillImpactSummaryDto {
  billId: string;
  billCode: string;
  title: string;
  summary?: string | null;
  jurisdictionId: string;
  impactDirection: string;
  confidence: number;
  rationale?: string | null;
}

interface PrincipleListItemDto {
  id: string;
  slug: string;
  name: string;
  summary?: string | null;
  isSignable: boolean;
  causeCount: number;
  billImpactCount: number;
  positionStatementCount: number;
}

interface SupportAggregateDto {
  id: string;
  targetType: string;
  targetId: string;
  jurisdictionId: string;
  districtId?: string | null;
  verificationLevel: string;
  snapshotDate: string;
  signatureCount: number;
  supporterCount: number;
}

interface PrincipleDetailDto {
  id: string;
  slug: string;
  name: string;
  summary?: string | null;
  description?: string | null;
  isSignable: boolean;
  positionStatements: PositionStatementSummaryDto[];
  causes: CauseSummaryDto[];
  issues: CauseIssueDto[];
  billImpacts: PrincipleBillImpactSummaryDto[];
  supportAggregates: SupportAggregateDto[];
}

interface OfficialPageDto {
  id: string;
  slug: string;
  headline?: string | null;
  summary?: string | null;
}

interface OfficialTermDto {
  officeName: string;
  districtName?: string | null;
  party?: string | null;
  startDate?: string | null;
  endDate?: string | null;
  isCurrent: boolean;
}

interface OfficialPositionRecordDto {
  id: string;
  positionType: string;
  summary: string;
  sourceReference?: string | null;
  recordedUtc: string;
  billId?: string | null;
  causeId?: string | null;
  principleId?: string | null;
  positionStatementId?: string | null;
}

interface OfficialResponseDetailDto {
  id: string;
  responseText: string;
  respondedUtc: string;
}

interface PublicQuestionDetailDto {
  id: string;
  questionText: string;
  status: string;
  createdUtc: string;
  responses: OfficialResponseDetailDto[];
}

interface ContradictionRecordDto {
  id: string;
  title: string;
  summary: string;
  severity: string;
  createdUtc: string;
}

interface OfficialScorecardDto {
  id: string;
  score: number;
  summary: string;
  versionNumber: number;
  createdUtc: string;
}

interface OfficialDetailDto {
  id: string;
  fullName: string;
  party?: string | null;
  stateCode?: string | null;
  page?: OfficialPageDto | null;
  terms: OfficialTermDto[];
  positionRecords: OfficialPositionRecordDto[];
  publicQuestions: PublicQuestionDetailDto[];
  contradictions: ContradictionRecordDto[];
  latestScorecard?: OfficialScorecardDto | null;
}

interface OfficialListItemDto {
  id: string;
  fullName: string;
  party?: string | null;
  stateCode?: string | null;
  jurisdictionName: string;
  officeName?: string | null;
  districtName?: string | null;
}

interface OfficialSummaryDto {
  id: string;
  fullName: string;
  party?: string | null;
  stateCode?: string | null;
  officeName?: string | null;
}

interface ActionPacketDto {
  id: string;
  title: string;
  body: string;
  status: string;
  versionNumber: number;
  createdUtc: string;
  templateKey?: string | null;
  officialId?: string | null;
  causeId?: string | null;
  billId?: string | null;
  districtId?: string | null;
  evidencePacketId?: string | null;
}

interface ActionPacketSummaryDto {
  id: string;
  title: string;
  status: string;
  versionNumber: number;
  createdUtc: string;
  templateKey?: string | null;
  officialId?: string | null;
  causeId?: string | null;
  billId?: string | null;
  evidencePacketId?: string | null;
}

interface BillReferenceDto {
  id: string;
  billCode: string;
  title: string;
  summary?: string | null;
  jurisdictionId: string;
}

interface EvidencePacketItemDetailDto {
  id: string;
  itemType: string;
  displayText: string;
  sortOrder: number;
  billId?: string | null;
  billVersionId?: string | null;
  sourceDocumentId?: string | null;
  recordDocumentId?: string | null;
  explanationId?: string | null;
  riskAssessmentId?: string | null;
}

interface EvidencePacketDetailDto {
  id: string;
  title: string;
  summary?: string | null;
  bill?: BillReferenceDto | null;
  cause?: CauseSummaryDto | null;
  positionStatement?: PositionStatementSummaryDto | null;
  items: EvidencePacketItemDetailDto[];
  officials: OfficialSummaryDto[];
  actionPackets: ActionPacketSummaryDto[];
}

interface SourceDocumentReferenceDto {
  id: string;
  title: string;
  sourceName: string;
}

interface SourceDocumentDetailDto {
  id: string;
  title: string;
  sourceName: string;
  documentType: string;
  sourceEntityType: string;
  sourceEntityId: string;
  sourceUrl?: string | null;
  mimeType?: string | null;
  retrievedUtc: string;
  createdUtc: string;
  updatedUtc: string;
  readableContent?: string | null;
  bill?: BillReferenceDto | null;
  evidencePackets: EvidencePacketReferenceDto[];
  causes: CauseSummaryDto[];
  officials: OfficialSummaryDto[];
  actionPackets: ActionPacketSummaryDto[];
}

interface RecordDocumentDetailDto {
  id: string;
  recordType: string;
  title: string;
  contentText?: string | null;
  createdUtc: string;
  updatedUtc: string;
  parentSourceDocument?: SourceDocumentReferenceDto | null;
  bill?: BillReferenceDto | null;
  causes: CauseSummaryDto[];
  officials: OfficialSummaryDto[];
  evidencePackets: EvidencePacketReferenceDto[];
  actionPackets: ActionPacketSummaryDto[];
}

interface ExplanationDetailDto {
  id: string;
  title: string;
  body: string;
  versionNumber: number;
  isCurrent: boolean;
  createdUtc: string;
  positionStatement: PositionStatementSummaryDto;
  bill?: BillReferenceDto | null;
  cause?: CauseSummaryDto | null;
  evidencePackets: EvidencePacketReferenceDto[];
  actionPackets: ActionPacketSummaryDto[];
}

interface RiskAssessmentDetailDto {
  id: string;
  title: string;
  severity: string;
  confidenceText?: string | null;
  narrative: string;
  versionNumber: number;
  isCurrent: boolean;
  createdUtc: string;
  positionStatement: PositionStatementSummaryDto;
  bill?: BillReferenceDto | null;
  cause?: CauseSummaryDto | null;
  evidencePackets: EvidencePacketReferenceDto[];
  actionPackets: ActionPacketSummaryDto[];
}

interface JurisdictionLookupDto {
  id: string;
  code: string;
  name: string;
  level: string;
  districts: DistrictLookupDto[];
}

interface DistrictLookupDto {
  id: string;
  code: string;
  name: string;
}

interface SignatureResponseDto {
  id: string;
  targetType: string;
  targetId: string;
  signatureState: string;
  verificationLevel: string;
  createdUtc: string;
}

interface SignatureTargetDefinition {
  targetId: string;
  targetType: CivicTargetType;
  label: string;
  description?: string | null;
  defaultJurisdictionId?: string | null;
}

interface CivicPageShell {
  root: HTMLElement;
  title: HTMLElement | null;
  kicker: HTMLElement | null;
  subhead: HTMLElement | null;
}

const apiBase = (() => {
  const globalWindow = window as Window & { UNBOUGHTPRESS_API_BASE?: string };
  const origin = window.location?.origin || '';
  if (globalWindow.UNBOUGHTPRESS_API_BASE) return globalWindow.UNBOUGHTPRESS_API_BASE;
  if (origin.includes('localhost') || origin.includes('127.0.0.1')) return origin;
  if (!origin || origin === 'null' || origin.startsWith('file:')) return 'http://localhost:5000';
  return 'https://api.unboughtpress.com';
})();

function escapeHtml(value: string | number | null | undefined): string {
  return String(value ?? '')
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}

function summarizeText(value: string | null | undefined, maxLength = 220): string {
  const normalized = String(value ?? '').trim().replace(/\s+/g, ' ');
  if (!normalized) return '';
  if (normalized.length <= maxLength) return normalized;
  return `${normalized.slice(0, maxLength - 1).trimEnd()}…`;
}

function humanizeSlug(value: string | null | undefined): string {
  return String(value ?? '')
    .trim()
    .replace(/[-_]+/g, ' ')
    .replace(/\b\w/g, (match) => match.toUpperCase());
}

function formatDateTime(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  });
}

function formatDateOnly(value: string | null | undefined): string {
  if (!value) return '';
  const date = new Date(`${value}T00:00:00`);
  if (Number.isNaN(date.getTime())) return '';
  return date.toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric'
  });
}

function ensureClientGuid(): string {
  const storageKey = 'up_client_guid';
  const fromStorage = window.localStorage.getItem(storageKey);
  const fromCookie = document.cookie
    .split(';')
    .map((cookie) => cookie.trim())
    .find((cookie) => cookie.startsWith('up_client_guid='))
    ?.split('=')[1];

  const clientGuid = fromStorage || fromCookie || (crypto.randomUUID ? crypto.randomUUID() : `${Date.now()}-${Math.random().toString(16).slice(2)}`);
  window.localStorage.setItem(storageKey, clientGuid);
  document.cookie = `up_client_guid=${clientGuid}; path=/; max-age=31536000; SameSite=None; Secure`;
  return clientGuid;
}

function clientHeaders(): Record<string, string> {
  const clientGuid = ensureClientGuid();
  return clientGuid ? { 'X-Client-Guid': clientGuid } : {};
}

async function requestApi<T>(path: string, init?: RequestInit): Promise<T> {
  const response = await fetch(`${apiBase}${path}`, {
    credentials: 'include',
    ...init,
    headers: {
      ...clientHeaders(),
      ...(init?.headers || {})
    }
  });

  if (!response.ok) {
    const text = await response.text();
    let message = text || `Request failed: ${response.status}`;
    try {
      const parsed = JSON.parse(text);
      if (parsed && typeof parsed.error === 'string') {
        message = parsed.error;
      }
    } catch {
      message = text || `Request failed: ${response.status}`;
    }
    throw new Error(message);
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json() as Promise<T>;
}

async function fetchBill(id: string): Promise<BillCivicDetailDto> {
  return requestApi<BillCivicDetailDto>(`/policycloud/bills/${encodeURIComponent(id)}/civic`);
}

async function fetchCause(id: string): Promise<CauseDetailDto> {
  return requestApi<CauseDetailDto>(`/policycloud/causes/${encodeURIComponent(id)}`);
}

async function fetchCauses(): Promise<CauseListItemDto[]> {
  return requestApi<CauseListItemDto[]>(`/policycloud/causes`);
}

async function fetchIssue(id: string): Promise<IssueDetailDto> {
  return requestApi<IssueDetailDto>(`/policycloud/issues/${encodeURIComponent(id)}`);
}

async function fetchIssues(): Promise<IssueListItemDto[]> {
  return requestApi<IssueListItemDto[]>(`/policycloud/issues`);
}

async function fetchEvidencePackets(): Promise<EvidencePacketListItemDto[]> {
  return requestApi<EvidencePacketListItemDto[]>(`/policycloud/evidence-packets`);
}

async function fetchPrinciple(id: string): Promise<PrincipleDetailDto> {
  return requestApi<PrincipleDetailDto>(`/policycloud/principles/${encodeURIComponent(id)}`);
}

async function fetchPrinciples(): Promise<PrincipleListItemDto[]> {
  return requestApi<PrincipleListItemDto[]>(`/policycloud/principles`);
}

async function fetchOfficial(id: string): Promise<OfficialDetailDto> {
  return requestApi<OfficialDetailDto>(`/policycloud/officials/${encodeURIComponent(id)}`);
}

async function fetchOfficials(): Promise<OfficialListItemDto[]> {
  return requestApi<OfficialListItemDto[]>(`/policycloud/officials`);
}

async function fetchActionPacket(id: string): Promise<ActionPacketDto> {
  return requestApi<ActionPacketDto>(`/policycloud/action-packets/${encodeURIComponent(id)}`);
}

async function fetchEvidencePacket(id: string): Promise<EvidencePacketDetailDto> {
  return requestApi<EvidencePacketDetailDto>(`/policycloud/evidence-packets/${encodeURIComponent(id)}`);
}

async function fetchSourceDocument(id: string): Promise<SourceDocumentDetailDto> {
  return requestApi<SourceDocumentDetailDto>(`/policycloud/source-documents/${encodeURIComponent(id)}`);
}

async function fetchRecordDocument(id: string): Promise<RecordDocumentDetailDto> {
  return requestApi<RecordDocumentDetailDto>(`/policycloud/record-documents/${encodeURIComponent(id)}`);
}

async function fetchExplanation(id: string): Promise<ExplanationDetailDto> {
  return requestApi<ExplanationDetailDto>(`/policycloud/explanations/${encodeURIComponent(id)}`);
}

async function fetchRiskAssessment(id: string): Promise<RiskAssessmentDetailDto> {
  return requestApi<RiskAssessmentDetailDto>(`/policycloud/risk-assessments/${encodeURIComponent(id)}`);
}

async function fetchActionPackets(params: Record<string, string>): Promise<ActionPacketDto[]> {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) searchParams.set(key, value);
  });
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
  return requestApi<ActionPacketDto[]>(`/policycloud/action-packets${suffix}`);
}

async function fetchSupportAggregates(params: Record<string, string>): Promise<SupportAggregateDto[]> {
  const searchParams = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value) searchParams.set(key, value);
  });
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : '';
  return requestApi<SupportAggregateDto[]>(`/policycloud/support/aggregates${suffix}`);
}

async function fetchGeography(): Promise<JurisdictionLookupDto[]> {
  return requestApi<JurisdictionLookupDto[]>(`/policycloud/geography`);
}

async function createSignature(payload: {
  targetType: CivicTargetType;
  targetId: string;
  email: string;
  displayName?: string | null;
  jurisdictionId: string;
  districtId?: string | null;
  verificationLevel: VerificationLevel;
  displayText?: string | null;
  postalCode?: string | null;
}): Promise<SignatureResponseDto> {
  return requestApi<SignatureResponseDto>(`/policycloud/signatures`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json'
    },
    body: JSON.stringify({
      targetType: payload.targetType,
      targetId: payload.targetId,
      email: payload.email,
      displayName: payload.displayName ?? null,
      jurisdictionId: payload.jurisdictionId,
      districtId: payload.districtId ?? null,
      verificationLevel: payload.verificationLevel,
      verificationMethod: 'civic-ui',
      signatureState: 'active',
      displayText: payload.displayText ?? null,
      postalCode: payload.postalCode ?? null
    })
  });
}

function readQueryParam(key: string): string {
  return new URLSearchParams(window.location.search).get(key)?.trim() || '';
}

function uniqueValues(values: Array<string | null | undefined>): string[] {
  return Array.from(new Set(values.filter((value): value is string => Boolean(value))));
}

function billHref(id: string): string {
  return `bill.html?id=${encodeURIComponent(id)}`;
}

function causeHref(id: string): string {
  return `cause.html?id=${encodeURIComponent(id)}`;
}

function issueHref(id: string): string {
  return `issue.html?id=${encodeURIComponent(id)}`;
}

function principleHref(id: string): string {
  return `principle.html?id=${encodeURIComponent(id)}`;
}

function officialHref(id: string): string {
  return `official.html?id=${encodeURIComponent(id)}`;
}

function evidencePacketsHref(): string {
  return 'evidence-packets.html';
}

function actionPacketHref(id: string): string {
  return `action-packet.html?id=${encodeURIComponent(id)}`;
}

function evidencePacketHref(id: string): string {
  return `evidence-packet.html?id=${encodeURIComponent(id)}`;
}

function sourceDocumentHref(id: string): string {
  return `source-document.html?id=${encodeURIComponent(id)}`;
}

function recordDocumentHref(id: string): string {
  return `record-document.html?id=${encodeURIComponent(id)}`;
}

function explanationHref(id: string): string {
  return `explanation.html?id=${encodeURIComponent(id)}`;
}

function riskAssessmentHref(id: string): string {
  return `risk-assessment.html?id=${encodeURIComponent(id)}`;
}

function renderMetaPills(items: Array<string | null | undefined>): string {
  const filteredItems = items.filter((item): item is string => Boolean(item && item.trim()));
  if (!filteredItems.length) return '';
  return `<div class="civic-pill-row">${filteredItems.map((item) => `<span class="civic-pill">${escapeHtml(item)}</span>`).join('')}</div>`;
}

function renderCard(title: string, body: string, kicker?: string): string {
  return `
    <section class="civic-card glass">
      ${kicker ? `<p class="civic-card-kicker">${escapeHtml(kicker)}</p>` : ''}
      <h3 class="civic-card-title">${escapeHtml(title)}</h3>
      <div class="civic-card-body">${body}</div>
    </section>
  `;
}

function renderSummaryBand(items: Array<{ label: string; text: string; links?: Array<{ href: string; label: string }> }>): string {
  return `
    <section class="civic-summary-band">
      ${items.map((item) => `
        <article class="civic-summary-item glass">
          <p class="civic-card-kicker">${escapeHtml(item.label)}</p>
          <p class="civic-summary-text">${escapeHtml(item.text)}</p>
          ${item.links?.length ? `
            <div class="civic-summary-links">
              ${item.links.map((link) => `<a class="civic-reference-link" href="${escapeHtml(link.href)}">${escapeHtml(link.label)}</a>`).join('')}
            </div>
          ` : ''}
        </article>
      `).join('')}
    </section>
  `;
}

function renderDiscoverySummary(title: string, summary: string, secondary: string, actionHref: string, actionLabel: string): string {
  return renderSummaryBand([
    { label: 'What This Means', text: summary },
    { label: 'How To Use It', text: secondary, links: [{ href: actionHref, label: actionLabel }] }
  ]);
}

function renderChecklist(items: string[]): string {
  return `
    <div class="civic-checklist">
      ${items.map((item) => `<p>${escapeHtml(item)}</p>`).join('')}
    </div>
  `;
}

function renderEmptyState(message: string): string {
  return `<p class="civic-empty">${escapeHtml(message)}</p>`;
}

function renderRichText(text: string | null | undefined): string {
  const normalized = String(text ?? '').trim();
  if (!normalized) return '';
  return normalized
    .split(/\n{2,}/)
    .map((paragraph) => `<p>${escapeHtml(paragraph.trim())}</p>`)
    .join('');
}

function renderDocumentContent(text: string | null | undefined, emptyMessage: string): string {
  const normalized = String(text ?? '').trim();
  if (!normalized) {
    return renderEmptyState(emptyMessage);
  }

  if (normalized.startsWith('{') || normalized.startsWith('[')) {
    return `<pre class="civic-document-pre">${escapeHtml(normalized)}</pre>`;
  }

  return `
    <div class="civic-body-prose">
      ${normalized
        .split(/\n{2,}/)
        .map((paragraph) => `<p>${escapeHtml(paragraph.trim())}</p>`)
        .join('')}
    </div>
  `;
}

function dedupeLatestStatements<T extends { statementGroupId: string; versionNumber: number; createdUtc: string }>(statements: T[]): T[] {
  const orderedStatements = [...statements].sort((left, right) => {
    if (right.versionNumber !== left.versionNumber) return right.versionNumber - left.versionNumber;
    return new Date(right.createdUtc).getTime() - new Date(left.createdUtc).getTime();
  });
  const latestByGroup = new Map<string, T>();
  orderedStatements.forEach((statement) => {
    if (!latestByGroup.has(statement.statementGroupId)) {
      latestByGroup.set(statement.statementGroupId, statement);
    }
  });
  return Array.from(latestByGroup.values());
}

function chooseDefaultJurisdictionId(jurisdictionIds: Array<string | null | undefined>): string | null {
  const normalizedJurisdictionIds = uniqueValues(jurisdictionIds);
  return normalizedJurisdictionIds.length === 1 ? normalizedJurisdictionIds[0] : null;
}

function formatConfidence(confidence: number | null | undefined): string {
  if (typeof confidence !== 'number' || Number.isNaN(confidence)) return '';
  return `${Math.round(confidence * 100)}% confidence`;
}

function renderSupportAggregateBlock(aggregates: SupportAggregateDto[]): string {
  if (!aggregates.length) {
    return renderEmptyState('No support snapshot has been recorded yet.');
  }

  const latestSnapshotDate = aggregates
    .map((aggregate) => aggregate.snapshotDate)
    .sort()
    .reverse()[0];

  const latestAggregates = aggregates
    .filter((aggregate) => aggregate.snapshotDate === latestSnapshotDate)
    .sort((left, right) => right.signatureCount - left.signatureCount);

  return `
    <div class="civic-stat-grid">
      ${latestAggregates.map((aggregate) => `
        <div class="civic-stat">
          <span class="civic-stat-label">${escapeHtml(humanizeSlug(aggregate.verificationLevel))}</span>
          <strong>${aggregate.signatureCount.toLocaleString()}</strong>
          <span>${aggregate.supporterCount.toLocaleString()} supporters</span>
        </div>
      `).join('')}
    </div>
    <p class="civic-note">Latest snapshot: ${escapeHtml(formatDateOnly(latestSnapshotDate))}</p>
  `;
}

function renderEntityLinks(items: Array<{ href: string; title: string; meta?: string | null; body?: string | null }>, emptyMessage: string): string {
  if (!items.length) {
    return renderEmptyState(emptyMessage);
  }

  return `
    <div class="civic-entity-list">
      ${items.map((item) => `
        <article class="civic-entity-card">
          <div class="civic-entity-body">
            <h4><a href="${escapeHtml(item.href)}">${escapeHtml(item.title)}</a></h4>
            ${item.meta ? `<p class="civic-entity-meta">${escapeHtml(item.meta)}</p>` : ''}
            ${item.body ? `<p>${escapeHtml(item.body)}</p>` : ''}
          </div>
        </article>
      `).join('')}
    </div>
  `;
}

function renderDiscoveryCards(items: Array<{
  href: string;
  title: string;
  summary?: string | null;
  pills: string[];
  ctaLabel: string;
}>, emptyMessage: string): string {
  if (!items.length) {
    return renderEmptyState(emptyMessage);
  }

  return `
    <div class="civic-discovery-grid">
      ${items.map((item) => `
        <article class="civic-discovery-card glass">
          <div class="civic-discovery-body">
            ${renderMetaPills(item.pills)}
            <h3><a href="${escapeHtml(item.href)}">${escapeHtml(item.title)}</a></h3>
            <p>${escapeHtml(item.summary || 'No summary available yet.')}</p>
          </div>
          <div class="civic-button-row">
            <a class="policy-cloud-action-btn is-accent" href="${escapeHtml(item.href)}">${escapeHtml(item.ctaLabel)}</a>
          </div>
        </article>
      `).join('')}
    </div>
  `;
}

function renderEvidencePackets(evidencePackets: EvidencePacketSummaryDto[] | BillEvidencePacketDto[], emptyMessage: string): string {
  if (!evidencePackets.length) {
    return renderEmptyState(emptyMessage);
  }

  return `
    <div class="civic-entity-list">
      ${evidencePackets.map((evidencePacket) => `
        <article class="civic-entity-card">
          <div class="civic-entity-body">
            <h4><a href="${escapeHtml(evidencePacketHref('id' in evidencePacket ? evidencePacket.id : evidencePacket.evidencePacketId))}">${escapeHtml(evidencePacket.title)}</a></h4>
            ${evidencePacket.summary ? `<p>${escapeHtml(evidencePacket.summary)}</p>` : ''}
            ${'items' in evidencePacket && evidencePacket.items.length ? `<ul class="civic-inline-list">${evidencePacket.items.map((item) => `<li>${escapeHtml(item)}</li>`).join('')}</ul>` : ''}
          </div>
        </article>
      `).join('')}
    </div>
  `;
}

function renderEvidencePacketItems(items: EvidencePacketItemDetailDto[]): string {
  if (!items.length) {
    return renderEmptyState('No evidence items are attached yet.');
  }

  return `
    <div class="civic-entity-list">
      ${items.map((item) => {
        const primaryHref = item.sourceDocumentId
          ? sourceDocumentHref(item.sourceDocumentId)
          : item.recordDocumentId
            ? recordDocumentHref(item.recordDocumentId)
            : item.explanationId
              ? explanationHref(item.explanationId)
              : item.riskAssessmentId
                ? riskAssessmentHref(item.riskAssessmentId)
                : item.billId
                  ? billHref(item.billId)
                  : '';
        const references = [
          item.billId ? `<a class="civic-reference-link" href="${escapeHtml(billHref(item.billId))}">Bill</a>` : '',
          item.billVersionId ? `<span class="civic-reference-link">Bill Version</span>` : '',
          item.sourceDocumentId ? `<a class="civic-reference-link" href="${escapeHtml(sourceDocumentHref(item.sourceDocumentId))}">Source Document</a>` : '',
          item.recordDocumentId ? `<a class="civic-reference-link" href="${escapeHtml(recordDocumentHref(item.recordDocumentId))}">Record Document</a>` : '',
          item.explanationId ? `<a class="civic-reference-link" href="${escapeHtml(explanationHref(item.explanationId))}">Explanation</a>` : '',
          item.riskAssessmentId ? `<a class="civic-reference-link" href="${escapeHtml(riskAssessmentHref(item.riskAssessmentId))}">Risk Assessment</a>` : ''
        ].filter(Boolean).join('');

        return `
          <article class="civic-entity-card">
            <div class="civic-entity-body">
              <div class="civic-entity-head">
                <h4>${primaryHref ? `<a href="${escapeHtml(primaryHref)}">${escapeHtml(item.displayText)}</a>` : escapeHtml(item.displayText)}</h4>
                <div class="civic-pill-row">
                  <span class="civic-pill">${escapeHtml(humanizeSlug(item.itemType))}</span>
                  <span class="civic-pill">Order ${item.sortOrder}</span>
                </div>
              </div>
              ${references ? `<div class="civic-reference-row">${references}</div>` : ''}
            </div>
          </article>
        `;
      }).join('')}
    </div>
  `;
}

function renderPositionStatements(
  statements: PositionStatementSummaryDto[] | BillPositionStatementDto[],
  currentPageKind: CivicPageKind
): string {
  if (!statements.length) {
    return renderEmptyState('No position statements are attached yet.');
  }

  return `
    <div class="civic-entity-list">
      ${statements.map((statement) => {
        const relationLinks = [
          'causeId' in statement && statement.causeId && currentPageKind !== 'cause'
            ? `<a class="civic-reference-link" href="${escapeHtml(causeHref(statement.causeId))}">Cause</a>`
            : '',
          'principleId' in statement && statement.principleId && currentPageKind !== 'principle'
            ? `<a class="civic-reference-link" href="${escapeHtml(principleHref(statement.principleId))}">Principle</a>`
            : '',
          'billId' in statement && statement.billId && currentPageKind !== 'bill'
            ? `<a class="civic-reference-link" href="${escapeHtml(billHref(statement.billId))}">Bill</a>`
            : ''
        ].filter(Boolean).join('');

        return `
          <article class="civic-entity-card">
            <div class="civic-entity-body">
              <div class="civic-entity-head">
                <h4>${escapeHtml(statement.title)}</h4>
                <div class="civic-pill-row">
                  <span class="civic-pill">${escapeHtml(humanizeSlug(statement.positionType))}</span>
                  <span class="civic-pill">v${statement.versionNumber}</span>
                </div>
              </div>
              <p>${escapeHtml(statement.summary)}</p>
              <div class="civic-reference-row">
                <span class="civic-entity-meta">${escapeHtml(formatDateTime(statement.createdUtc))}</span>
                ${relationLinks}
              </div>
              ${statement.isSignable ? `
                <div class="civic-button-row">
                  <button
                    type="button"
                    class="policy-cloud-action-btn is-accent"
                    data-sign-target-id="${escapeHtml('id' in statement ? statement.id : statement.positionStatementId)}"
                    data-sign-target-type="position-statement"
                    data-sign-target-label="${escapeHtml(statement.title)}"
                    data-sign-target-description="${escapeHtml(statement.summary)}">Sign this statement</button>
                </div>
              ` : ''}
            </div>
          </article>
        `;
      }).join('')}
    </div>
  `;
}

function renderActionPackets(actionPackets: Array<ActionPacketDto | ActionPacketSummaryDto>): string {
  if (!actionPackets.length) {
    return renderEmptyState('No action packets have been generated yet.');
  }

  return renderEntityLinks(
    actionPackets.map((actionPacket) => ({
      href: actionPacketHref(actionPacket.id),
      title: actionPacket.title,
      meta: `${humanizeSlug(actionPacket.templateKey || 'packet')} · ${humanizeSlug(actionPacket.status)} · v${actionPacket.versionNumber}`,
      body: 'body' in actionPacket
        ? summarizeText(actionPacket.body)
        : formatDateTime(actionPacket.createdUtc)
    })),
    'No action packets have been generated yet.'
  );
}

function renderBillImpacts(billImpacts: PrincipleBillImpactSummaryDto[]): string {
  if (!billImpacts.length) {
    return renderEmptyState('No bill impacts have been linked yet.');
  }

  return renderEntityLinks(
    billImpacts.map((billImpact) => ({
      href: billHref(billImpact.billId),
      title: `${billImpact.billCode} · ${billImpact.title}`,
      meta: `${humanizeSlug(billImpact.impactDirection)} · ${formatConfidence(billImpact.confidence)}`,
      body: billImpact.summary || billImpact.rationale || ''
    })),
    'No bill impacts have been linked yet.'
  );
}

function renderOfficialTerms(terms: OfficialTermDto[]): string {
  if (!terms.length) {
    return renderEmptyState('No term history has been recorded yet.');
  }

  const orderedTerms = [...terms].sort((left, right) => Number(right.isCurrent) - Number(left.isCurrent));

  return `
    <div class="civic-entity-list">
      ${orderedTerms.map((term) => `
        <article class="civic-entity-card">
          <div class="civic-entity-body">
            <div class="civic-entity-head">
              <h4>${escapeHtml(term.officeName)}</h4>
              ${term.isCurrent ? '<span class="civic-pill civic-pill-accent">Current</span>' : ''}
            </div>
            ${term.districtName ? `<p>${escapeHtml(term.districtName)}</p>` : ''}
            ${renderMetaPills([
              term.party,
              term.startDate ? `Started ${formatDateOnly(term.startDate)}` : null,
              term.endDate ? `Ended ${formatDateOnly(term.endDate)}` : 'Open term'
            ])}
          </div>
        </article>
      `).join('')}
    </div>
  `;
}

function renderPositionRecords(positionRecords: OfficialPositionRecordDto[]): string {
  if (!positionRecords.length) {
    return renderEmptyState('No public position records have been attached yet.');
  }

  return `
    <div class="civic-entity-list">
      ${positionRecords.map((positionRecord) => {
        const references = [
          positionRecord.billId ? `<a class="civic-reference-link" href="${escapeHtml(billHref(positionRecord.billId))}">Bill</a>` : '',
          positionRecord.causeId ? `<a class="civic-reference-link" href="${escapeHtml(causeHref(positionRecord.causeId))}">Cause</a>` : '',
          positionRecord.principleId ? `<a class="civic-reference-link" href="${escapeHtml(principleHref(positionRecord.principleId))}">Principle</a>` : '',
          positionRecord.sourceReference ? `<a class="civic-reference-link" href="${escapeHtml(positionRecord.sourceReference)}" target="_blank" rel="noopener">Source</a>` : ''
        ].filter(Boolean).join('');

        return `
          <article class="civic-entity-card">
            <div class="civic-entity-body">
              <div class="civic-entity-head">
                <h4>${escapeHtml(humanizeSlug(positionRecord.positionType))}</h4>
                <span class="civic-entity-meta">${escapeHtml(formatDateTime(positionRecord.recordedUtc))}</span>
              </div>
              <p>${escapeHtml(positionRecord.summary)}</p>
              ${references ? `<div class="civic-reference-row">${references}</div>` : ''}
            </div>
          </article>
        `;
      }).join('')}
    </div>
  `;
}

function renderQuestions(questions: PublicQuestionDetailDto[]): string {
  if (!questions.length) {
    return renderEmptyState('No public questions have been published yet.');
  }

  return `
    <div class="civic-entity-list">
      ${questions.map((question) => `
        <article class="civic-entity-card">
          <div class="civic-entity-body">
            <div class="civic-entity-head">
              <h4>${escapeHtml(question.questionText)}</h4>
              <span class="civic-pill">${escapeHtml(humanizeSlug(question.status))}</span>
            </div>
            <p class="civic-entity-meta">${escapeHtml(formatDateTime(question.createdUtc))}</p>
            ${question.responses.length ? `
              <div class="civic-response-list">
                ${question.responses.map((response) => `
                  <div class="civic-response-card">
                    <p>${escapeHtml(response.responseText)}</p>
                    <span>${escapeHtml(formatDateTime(response.respondedUtc))}</span>
                  </div>
                `).join('')}
              </div>
            ` : '<p class="civic-empty">No official response has been posted yet.</p>'}
          </div>
        </article>
      `).join('')}
    </div>
  `;
}

function renderContradictions(contradictions: ContradictionRecordDto[]): string {
  if (!contradictions.length) {
    return renderEmptyState('No contradiction records are attached yet.');
  }

  return `
    <div class="civic-entity-list">
      ${contradictions.map((contradiction) => `
        <article class="civic-entity-card">
          <div class="civic-entity-body">
            <div class="civic-entity-head">
              <h4>${escapeHtml(contradiction.title)}</h4>
              <span class="civic-pill civic-pill-accent">${escapeHtml(humanizeSlug(contradiction.severity))}</span>
            </div>
            <p>${escapeHtml(contradiction.summary)}</p>
            <span class="civic-entity-meta">${escapeHtml(formatDateTime(contradiction.createdUtc))}</span>
          </div>
        </article>
      `).join('')}
    </div>
  `;
}

function renderActionPacketBody(body: string): string {
  const normalized = String(body ?? '').trim();
  if (!normalized) {
    return renderEmptyState('The packet body is empty.');
  }

  return `
    <div class="civic-body-prose">
      ${normalized.split(/\n{2,}/).map((paragraph) => `<p>${escapeHtml(paragraph.trim())}</p>`).join('')}
    </div>
  `;
}

function setPageShell(shell: CivicPageShell, title: string, kicker: string, subhead: string): void {
  if (shell.title) shell.title.textContent = title;
  if (shell.kicker) shell.kicker.textContent = kicker;
  if (shell.subhead) shell.subhead.textContent = subhead;
}

function setDocumentTitle(title: string): void {
  document.title = `${title} | Unbought Press`;
}

function buildSignatureFormHtml(
  target: SignatureTargetDefinition,
  jurisdictions: JurisdictionLookupDto[],
  jurisdictionId: string,
  districtId: string,
  status: { tone: 'success' | 'error' | 'info' | ''; text: string }
): string {
  const selectedJurisdiction = jurisdictions.find((jurisdiction) => jurisdiction.id === jurisdictionId) || null;
  const districtOptions = selectedJurisdiction?.districts || [];
  const isDisabled = jurisdictions.length === 0;

  return `
    <div class="civic-signature-target">
      <p class="civic-card-kicker">Signing target</p>
      <h4>${escapeHtml(target.label)}</h4>
      ${target.description ? `<p>${escapeHtml(target.description)}</p>` : ''}
      <p class="civic-note">${escapeHtml(humanizeSlug(target.targetType))}</p>
    </div>
    <form class="civic-signature-form" data-signature-form novalidate>
      <label class="policy-cloud-field">
        <span class="policy-cloud-field-label">Email</span>
        <input class="policy-cloud-input" name="email" type="email" autocomplete="email" required />
      </label>
      <label class="policy-cloud-field">
        <span class="policy-cloud-field-label">Display name</span>
        <input class="policy-cloud-input" name="displayName" type="text" autocomplete="name" placeholder="Optional for private support" />
      </label>
      <label class="policy-cloud-field">
        <span class="policy-cloud-field-label">Visibility</span>
        <select class="policy-cloud-input" name="visibilityMode">
          <option value="private">Private record</option>
          <option value="first-name">First name only</option>
          <option value="full-name">Full name</option>
        </select>
      </label>
      <label class="policy-cloud-field">
        <span class="policy-cloud-field-label">Jurisdiction</span>
        <select class="policy-cloud-input" name="jurisdictionId" ${isDisabled ? 'disabled' : ''}>
          <option value="">Select jurisdiction</option>
          ${jurisdictions.map((jurisdiction) => `
            <option value="${escapeHtml(jurisdiction.id)}" ${jurisdiction.id === jurisdictionId ? 'selected' : ''}>${escapeHtml(jurisdiction.name)}</option>
          `).join('')}
        </select>
      </label>
      <label class="policy-cloud-field">
        <span class="policy-cloud-field-label">District</span>
        <select class="policy-cloud-input" name="districtId" ${!districtOptions.length ? 'disabled' : ''}>
          <option value="">No district</option>
          ${districtOptions.map((district) => `
            <option value="${escapeHtml(district.id)}" ${district.id === districtId ? 'selected' : ''}>${escapeHtml(district.name)}</option>
          `).join('')}
        </select>
      </label>
      <label class="policy-cloud-field">
        <span class="policy-cloud-field-label">Verification level</span>
        <select class="policy-cloud-input" name="verificationLevel">
          <option value="verified">Verified</option>
          <option value="unverified">Unverified</option>
          <option value="residency-verified">Residency verified</option>
          <option value="identity-verified">Identity verified</option>
        </select>
      </label>
      <label class="policy-cloud-field">
        <span class="policy-cloud-field-label">Postal code</span>
        <input class="policy-cloud-input" name="postalCode" type="text" inputmode="numeric" autocomplete="postal-code" />
      </label>
      <label class="policy-cloud-field">
        <span class="policy-cloud-field-label">Public note</span>
        <textarea class="policy-cloud-input civic-textarea" name="displayText" rows="4" placeholder="Optional public support note"></textarea>
      </label>
      ${status.text ? `<p class="civic-signature-status ${status.tone ? `is-${status.tone}` : ''}">${escapeHtml(status.text)}</p>` : ''}
      <div class="civic-button-row">
        <button type="submit" class="policy-cloud-action-btn is-accent" ${isDisabled ? 'disabled' : ''}>Record support</button>
      </div>
    </form>
  `;
}

function attachSignaturePanel(
  root: HTMLElement,
  host: HTMLElement,
  targets: SignatureTargetDefinition[],
  jurisdictions: JurisdictionLookupDto[],
  defaultJurisdictionId: string | null,
  onSuccess?: () => Promise<void>
): void {
  if (!targets.length) {
    host.innerHTML = renderEmptyState('This record is not signable yet.');
    return;
  }

  const requestedTargetId = readQueryParam('sign');
  let currentTarget = targets.find((target) => target.targetId === requestedTargetId) || targets[0];
  let currentJurisdictionId = currentTarget.defaultJurisdictionId || defaultJurisdictionId || '';
  let currentDistrictId = '';
  let status = { tone: '' as '' | 'success' | 'error' | 'info', text: '' };

  const render = () => {
    host.innerHTML = buildSignatureFormHtml(
      currentTarget,
      jurisdictions,
      currentJurisdictionId,
      currentDistrictId,
      status
    );

    const form = host.querySelector<HTMLFormElement>('[data-signature-form]');
    const jurisdictionSelect = host.querySelector<HTMLSelectElement>('select[name="jurisdictionId"]');
    const districtSelect = host.querySelector<HTMLSelectElement>('select[name="districtId"]');

    jurisdictionSelect?.addEventListener('change', () => {
      currentJurisdictionId = jurisdictionSelect.value;
      currentDistrictId = '';
      status = { tone: '', text: '' };
      render();
    });

    districtSelect?.addEventListener('change', () => {
      currentDistrictId = districtSelect.value;
    });

    form?.addEventListener('submit', async (event) => {
      event.preventDefault();
      const formData = new FormData(form);
      const email = String(formData.get('email') || '').trim();
      const rawDisplayName = String(formData.get('displayName') || '').trim();
      const visibilityMode = String(formData.get('visibilityMode') || 'private') as VisibilityMode;
      const verificationLevel = String(formData.get('verificationLevel') || 'verified') as VerificationLevel;
      const displayText = String(formData.get('displayText') || '').trim();
      const postalCode = String(formData.get('postalCode') || '').trim();
      const jurisdictionId = String(formData.get('jurisdictionId') || '').trim();
      const districtId = String(formData.get('districtId') || '').trim();

      if (!email) {
        status = { tone: 'error', text: 'Email is required to record support.' };
        render();
        return;
      }

      if (!jurisdictionId) {
        status = { tone: 'error', text: 'Choose a jurisdiction before submitting.' };
        render();
        return;
      }

      let displayName: string | null = null;
      if (visibilityMode === 'first-name' || visibilityMode === 'full-name') {
        if (!rawDisplayName) {
          status = { tone: 'error', text: 'A display name is required for public visibility.' };
          render();
          return;
        }
        displayName = visibilityMode === 'first-name'
          ? rawDisplayName.split(/\s+/)[0]
          : rawDisplayName;
      }

      status = { tone: 'info', text: 'Recording support…' };
      render();

      try {
        await createSignature({
          targetType: currentTarget.targetType,
          targetId: currentTarget.targetId,
          email,
          displayName,
          jurisdictionId,
          districtId: districtId || null,
          verificationLevel,
          displayText: visibilityMode === 'private' ? null : displayText || null,
          postalCode: postalCode || null
        });

        if (onSuccess) {
          await onSuccess();
        }

        status = { tone: 'success', text: 'Support recorded successfully.' };
        currentJurisdictionId = jurisdictionId;
        currentDistrictId = districtId;
        render();
      } catch (error) {
        status = { tone: 'error', text: error instanceof Error ? error.message : 'Unable to record support right now.' };
        render();
      }
    });
  };

  root.querySelectorAll<HTMLElement>('[data-sign-target-id]').forEach((button) => {
    button.addEventListener('click', () => {
      const targetId = button.dataset.signTargetId || '';
      const targetType = (button.dataset.signTargetType || 'cause') as CivicTargetType;
      const targetLabel = button.dataset.signTargetLabel || 'Support target';
      const targetDescription = button.dataset.signTargetDescription || '';
      const replacementTarget = targets.find((target) => target.targetId === targetId)
        || {
          targetId,
          targetType,
          label: targetLabel,
          description: targetDescription,
          defaultJurisdictionId: currentJurisdictionId
        };
      currentTarget = replacementTarget;
      currentJurisdictionId = replacementTarget.defaultJurisdictionId || currentJurisdictionId;
      status = { tone: '', text: '' };
      render();
      host.scrollIntoView({ behavior: 'smooth', block: 'start' });
    });
  });

  render();
}

async function fetchBillSafe(id: string): Promise<BillCivicDetailDto | null> {
  try {
    return await fetchBill(id);
  } catch {
    return null;
  }
}

async function fetchOfficialSafe(id: string): Promise<OfficialDetailDto | null> {
  try {
    return await fetchOfficial(id);
  } catch {
    return null;
  }
}

function renderLoading(root: HTMLElement, message: string): void {
  root.innerHTML = `<div class="glass civic-loading-card">${escapeHtml(message)}</div>`;
}

function renderFailure(root: HTMLElement, message: string): void {
  root.innerHTML = `
    <div class="glass civic-loading-card civic-error-card">
      <h3>Unable to load this record</h3>
      <p>${escapeHtml(message)}</p>
      <a class="policy-cloud-action-btn" href="index.html#policy-cloud-hero">Back to Policy Cloud</a>
    </div>
  `;
}

async function mountCausesPage(shell: CivicPageShell): Promise<void> {
  setPageShell(shell, 'Causes', 'Cause Discovery', 'Browse organized civic causes built from issues, principles, and position statements.');
  setDocumentTitle('Causes');
  renderLoading(shell.root, 'Loading causes…');

  const causes = await fetchCauses();

  setPageShell(shell, 'Cause Discovery', 'Cause Discovery', 'Browse organized civic causes built from issues, principles, and position statements.');
  setDocumentTitle('Cause Discovery');

  shell.root.innerHTML = `
    ${renderDiscoverySummary(
      'Cause discovery',
      'Causes turn issues and principles into concrete civic objects residents can organize around.',
      'Use this page to find an active cause, inspect its linked bills and officials, then record support or open an action packet.',
      'index.html#policy-cloud-hero',
      'Back to Explore'
    )}
    ${renderCard('Browse causes', `
      <div class="civic-filter-bar">
        <label class="policy-cloud-field civic-filter-field">
          <span class="policy-cloud-field-label">Search causes</span>
          <input class="policy-cloud-input" type="search" data-discovery-search placeholder="Search cause names or summaries" />
        </label>
        <label class="civic-toggle">
          <input type="checkbox" data-discovery-active-only checked />
          <span>Active only</span>
        </label>
      </div>
      <p class="civic-note" data-discovery-count></p>
      <div data-discovery-results></div>
    `, 'Discovery')}
  `;

  const resultsHost = shell.root.querySelector<HTMLElement>('[data-discovery-results]');
  const countHost = shell.root.querySelector<HTMLElement>('[data-discovery-count]');
  const searchInput = shell.root.querySelector<HTMLInputElement>('[data-discovery-search]');
  const activeOnlyInput = shell.root.querySelector<HTMLInputElement>('[data-discovery-active-only]');

  const renderResults = () => {
    const searchTerm = (searchInput?.value || '').trim().toLowerCase();
    const activeOnly = Boolean(activeOnlyInput?.checked);
    const filteredCauses = causes.filter((cause) => {
      if (activeOnly && !cause.isActive) return false;
      if (!searchTerm) return true;
      return cause.name.toLowerCase().includes(searchTerm)
        || (cause.summary || '').toLowerCase().includes(searchTerm);
    });

    if (countHost) {
      countHost.textContent = `${filteredCauses.length} cause${filteredCauses.length === 1 ? '' : 's'} visible`;
    }

    if (resultsHost) {
      resultsHost.innerHTML = renderDiscoveryCards(
        filteredCauses.map((cause) => ({
          href: causeHref(cause.slug || cause.id),
          title: cause.name,
          summary: cause.summary,
          pills: [
            cause.isActive ? 'Active' : 'Inactive',
            `${cause.principleCount} principles`,
            `${cause.issueCount} issues`,
            `${cause.actionPacketCount} action packets`
          ],
          ctaLabel: 'Open cause file'
        })),
        'No causes match that filter.'
      );
    }
  };

  searchInput?.addEventListener('input', renderResults);
  activeOnlyInput?.addEventListener('change', renderResults);
  renderResults();
}

async function mountPrinciplesPage(shell: CivicPageShell): Promise<void> {
  setPageShell(shell, 'Principles', 'Principle Discovery', 'Browse durable civic rules used to interpret bills, causes, and accountability records.');
  setDocumentTitle('Principles');
  renderLoading(shell.root, 'Loading principles…');

  const principles = await fetchPrinciples();

  setPageShell(shell, 'Principle Discovery', 'Principle Discovery', 'Browse durable civic rules used to interpret bills, causes, and accountability records.');
  setDocumentTitle('Principle Discovery');

  shell.root.innerHTML = `
    ${renderDiscoverySummary(
      'Principle discovery',
      'Principles are durable civic standards, not party slogans. They provide the rule-set used to read bills and measure officials.',
      'Use this page to start with a principle, then move into related causes, bill impacts, or signable statements.',
      'index.html#policy-cloud-hero',
      'Back to Explore'
    )}
    ${renderCard('Browse principles', `
      <div class="civic-filter-bar">
        <label class="policy-cloud-field civic-filter-field">
          <span class="policy-cloud-field-label">Search principles</span>
          <input class="policy-cloud-input" type="search" data-discovery-search placeholder="Search principle names or summaries" />
        </label>
        <label class="civic-toggle">
          <input type="checkbox" data-discovery-signable-only checked />
          <span>Signable only</span>
        </label>
      </div>
      <p class="civic-note" data-discovery-count></p>
      <div data-discovery-results></div>
    `, 'Discovery')}
  `;

  const resultsHost = shell.root.querySelector<HTMLElement>('[data-discovery-results]');
  const countHost = shell.root.querySelector<HTMLElement>('[data-discovery-count]');
  const searchInput = shell.root.querySelector<HTMLInputElement>('[data-discovery-search]');
  const signableOnlyInput = shell.root.querySelector<HTMLInputElement>('[data-discovery-signable-only]');

  const renderResults = () => {
    const searchTerm = (searchInput?.value || '').trim().toLowerCase();
    const signableOnly = Boolean(signableOnlyInput?.checked);
    const filteredPrinciples = principles.filter((principle) => {
      if (signableOnly && !principle.isSignable) return false;
      if (!searchTerm) return true;
      return principle.name.toLowerCase().includes(searchTerm)
        || (principle.summary || '').toLowerCase().includes(searchTerm);
    });

    if (countHost) {
      countHost.textContent = `${filteredPrinciples.length} principle${filteredPrinciples.length === 1 ? '' : 's'} visible`;
    }

    if (resultsHost) {
      resultsHost.innerHTML = renderDiscoveryCards(
        filteredPrinciples.map((principle) => ({
          href: principleHref(principle.slug || principle.id),
          title: principle.name,
          summary: principle.summary,
          pills: [
            principle.isSignable ? 'Signable' : 'Read only',
            `${principle.causeCount} causes`,
            `${principle.billImpactCount} bill impacts`,
            `${principle.positionStatementCount} statements`
          ],
          ctaLabel: 'Open principle file'
        })),
        'No principles match that filter.'
      );
    }
  };

  searchInput?.addEventListener('input', renderResults);
  signableOnlyInput?.addEventListener('change', renderResults);
  renderResults();
}

async function mountIssuesPage(shell: CivicPageShell): Promise<void> {
  setPageShell(shell, 'Issues', 'Issue Discovery', 'Browse policy issues that connect bills, principles, causes, and action records.');
  setDocumentTitle('Issues');
  renderLoading(shell.root, 'Loading issues…');

  const issues = await fetchIssues();

  setPageShell(shell, 'Issue Discovery', 'Issue Discovery', 'Browse policy issues that connect bills, principles, causes, and action records.');
  setDocumentTitle('Issue Discovery');

  shell.root.innerHTML = `
    ${renderDiscoverySummary(
      'Issue discovery',
      'Issues are the entry points that connect legislative evidence to civic interpretation and action.',
      'Use this page to move from a policy issue into related causes, principles, bills, and action packets.',
      'index.html#policy-cloud-hero',
      'Back to Explore'
    )}
    ${renderCard('Browse issues', `
      <div class="civic-filter-bar">
        <label class="policy-cloud-field civic-filter-field">
          <span class="policy-cloud-field-label">Search issues</span>
          <input class="policy-cloud-input" type="search" data-discovery-search placeholder="Search issue names or summaries" />
        </label>
      </div>
      <p class="civic-note" data-discovery-count></p>
      <div data-discovery-results></div>
    `, 'Discovery')}
  `;

  const resultsHost = shell.root.querySelector<HTMLElement>('[data-discovery-results]');
  const countHost = shell.root.querySelector<HTMLElement>('[data-discovery-count]');
  const searchInput = shell.root.querySelector<HTMLInputElement>('[data-discovery-search]');

  const renderResults = () => {
    const searchTerm = (searchInput?.value || '').trim().toLowerCase();
    const filteredIssues = issues.filter((issue) => {
      if (!searchTerm) return true;
      return issue.name.toLowerCase().includes(searchTerm)
        || (issue.summary || '').toLowerCase().includes(searchTerm);
    });

    if (countHost) {
      countHost.textContent = `${filteredIssues.length} issue${filteredIssues.length === 1 ? '' : 's'} visible`;
    }

    if (resultsHost) {
      resultsHost.innerHTML = renderDiscoveryCards(
        filteredIssues.map((issue) => ({
          href: issueHref(issue.slug || issue.id),
          title: issue.name,
          summary: issue.summary,
          pills: [
            `${issue.billCount} bills`,
            `${issue.principleCount} principles`,
            `${issue.causeCount} causes`,
            `${issue.actionPacketCount} action packets`
          ],
          ctaLabel: 'Open issue file'
        })),
        'No issues match that filter.'
      );
    }
  };

  searchInput?.addEventListener('input', renderResults);
  renderResults();
}

async function mountIssuePage(shell: CivicPageShell, id: string): Promise<void> {
  setPageShell(shell, 'Loading issue…', 'Issue File', 'Policy issue connected to principles, causes, bills, and action records');
  setDocumentTitle('Issue');
  renderLoading(shell.root, 'Loading issue record…');

  const issue = await fetchIssue(id);
  const firstPrinciple = issue.principles[0] || null;
  const firstCause = issue.causes[0] || null;
  const firstBill = issue.bills[0] || null;
  const firstActionPacket = issue.actionPackets[0] || null;

  setPageShell(shell, issue.name, 'Issue File', issue.summary || 'Policy issue connected to the civic graph.');
  setDocumentTitle(`${issue.name} | Issue`);

  shell.root.innerHTML = `
    ${renderSummaryBand([
      {
        label: 'What This Means',
        text: summarizeText(issue.summary || 'This issue groups related bills, principles, causes, and action artifacts into one policy thread.')
      },
      {
        label: 'Graph Footprint',
        text: `${issue.principles.length} related principles, ${issue.causes.length} causes, ${issue.bills.length} linked bills, and ${issue.actionPackets.length} action packets are attached to this issue.`
      },
      {
        label: 'Next Move',
        text: 'Move from the issue into a principle or cause first, then inspect linked bills and any available action packets.',
        links: [
          firstPrinciple ? { href: principleHref(firstPrinciple.principleId), label: 'Open Principle' } : { href: 'principles.html', label: 'Browse Principles' },
          firstCause ? { href: causeHref(firstCause.id), label: 'Open Cause' } : firstBill ? { href: billHref(firstBill.billId), label: 'Open Bill' } : { href: 'issues.html', label: 'Browse Issues' }
        ]
      }
    ])}
    <div class="civic-layout">
      <div class="civic-main">
        ${renderCard('Issue brief', `
          ${renderMetaPills([
            `${issue.principles.length} principles`,
            `${issue.causes.length} causes`,
            `${issue.bills.length} bills`,
            `${issue.actionPackets.length} action packets`
          ])}
          <div class="civic-body-prose">
            ${renderRichText(issue.summary || 'No issue summary has been attached yet.')}
          </div>
        `, 'Overview')}
        ${renderCard('How to use this issue', renderChecklist([
          'Start with related principles to understand the durable standards in play.',
          'Use causes to see how this issue becomes a mobilizable civic object.',
          'Review linked bills and action packets before sharing or pressuring an office.'
        ]), 'Reading Guide')}
        ${renderCard('Related bills', renderEntityLinks(
          issue.bills.map((bill) => ({
            href: billHref(bill.billId),
            title: `${bill.billCode} · ${bill.title}`,
            meta: `${humanizeSlug(bill.linkType)} · ${formatConfidence(bill.confidence)}`,
            body: bill.summary || bill.rationale || ''
          })),
          'No linked bills have been attached yet.'
        ), 'Evidence')}
        ${renderCard('Action packets', renderActionPackets(issue.actionPackets), 'Organize')}
      </div>
      <aside class="civic-side">
        ${renderCard('Related principles', renderEntityLinks(
          issue.principles.map((principle) => ({
            href: principleHref(principle.principleId),
            title: principle.name,
            body: principle.summary || ''
          })),
          'No related principles have been linked yet.'
        ), 'Interpretation')}
        ${renderCard('Related causes', renderEntityLinks(
          issue.causes.map((cause) => ({
            href: causeHref(cause.id),
            title: cause.name,
            body: cause.summary || ''
          })),
          'No related causes have been linked yet.'
        ), 'Align')}
        ${renderCard('Cross-links', renderEntityLinks(
          [
            firstPrinciple ? { href: principleHref(firstPrinciple.principleId), title: firstPrinciple.name, body: firstPrinciple.summary || '' } : null,
            firstCause ? { href: causeHref(firstCause.id), title: firstCause.name, body: firstCause.summary || '' } : null,
            firstBill ? { href: billHref(firstBill.billId), title: `${firstBill.billCode} · ${firstBill.title}`, body: firstBill.summary || '' } : null,
            firstActionPacket ? { href: actionPacketHref(firstActionPacket.id), title: firstActionPacket.title, body: formatDateTime(firstActionPacket.createdUtc) } : null
          ].filter(Boolean) as Array<{ href: string; title: string; body?: string | null }>,
          'Related records will appear here as this issue gains more linked interpretation and action data.'
        ), 'Navigation')}
      </aside>
    </div>
  `;
}

async function mountOfficialsPage(shell: CivicPageShell): Promise<void> {
  setPageShell(shell, 'Officials', 'Official Discovery', 'Browse public officials through office, jurisdiction, and accountability context.');
  setDocumentTitle('Officials');
  renderLoading(shell.root, 'Loading officials…');

  const officials = await fetchOfficials();

  setPageShell(shell, 'Official Discovery', 'Official Discovery', 'Browse public officials through office, jurisdiction, and accountability context.');
  setDocumentTitle('Official Discovery');

  shell.root.innerHTML = `
    ${renderDiscoverySummary(
      'Official discovery',
      'Official files collect term history, public questions, contradiction records, scorecards, and linked action packets.',
      'Use this page to enter accountability records directly by officeholder, not only through bill or cause cross-links.',
      'index.html#policy-cloud-hero',
      'Back to Explore'
    )}
    ${renderCard('Browse officials', `
      <div class="civic-filter-bar">
        <label class="policy-cloud-field civic-filter-field">
          <span class="policy-cloud-field-label">Search officials</span>
          <input class="policy-cloud-input" type="search" data-discovery-search placeholder="Search official, office, district, or jurisdiction" />
        </label>
      </div>
      <p class="civic-note" data-discovery-count></p>
      <div data-discovery-results></div>
    `, 'Discovery')}
  `;

  const resultsHost = shell.root.querySelector<HTMLElement>('[data-discovery-results]');
  const countHost = shell.root.querySelector<HTMLElement>('[data-discovery-count]');
  const searchInput = shell.root.querySelector<HTMLInputElement>('[data-discovery-search]');

  const renderResults = () => {
    const searchTerm = (searchInput?.value || '').trim().toLowerCase();
    const filteredOfficials = officials.filter((official) => {
      if (!searchTerm) return true;
      return [
        official.fullName,
        official.officeName,
        official.districtName,
        official.jurisdictionName,
        official.party,
        official.stateCode
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(searchTerm));
    });

    if (countHost) {
      countHost.textContent = `${filteredOfficials.length} official${filteredOfficials.length === 1 ? '' : 's'} visible`;
    }

    if (resultsHost) {
      resultsHost.innerHTML = renderDiscoveryCards(
        filteredOfficials.map((official) => ({
          href: officialHref(official.id),
          title: official.fullName,
          summary: [official.officeName, official.districtName, official.jurisdictionName].filter(Boolean).join(' · '),
          pills: [
            official.party || 'No party label',
            official.stateCode || 'No state code',
            official.officeName || 'Office pending'
          ],
          ctaLabel: 'Open official file'
        })),
        'No officials match that filter.'
      );
    }
  };

  searchInput?.addEventListener('input', renderResults);
  renderResults();
}

async function mountEvidencePacketsPage(shell: CivicPageShell): Promise<void> {
  setPageShell(shell, 'Evidence Packets', 'Evidence Discovery', 'Browse reusable evidence bundles linked to bills, causes, and position statements.');
  setDocumentTitle('Evidence Packets');
  renderLoading(shell.root, 'Loading evidence packets…');

  const evidencePackets = await fetchEvidencePackets();

  setPageShell(shell, 'Evidence Discovery', 'Evidence Discovery', 'Browse reusable evidence bundles linked to bills, causes, and position statements.');
  setDocumentTitle('Evidence Discovery');

  shell.root.innerHTML = `
    ${renderDiscoverySummary(
      'Evidence packet discovery',
      'Evidence packets collect reusable source, record, explanation, and risk materials into one civic evidence bundle.',
      'Use this page to start from the evidence itself, then move into the linked bill, cause, or action packet records.',
      'index.html#policy-cloud-hero',
      'Back to Explore'
    )}
    ${renderCard('Browse evidence packets', `
      <div class="civic-filter-bar">
        <label class="policy-cloud-field civic-filter-field">
          <span class="policy-cloud-field-label">Search evidence packets</span>
          <input class="policy-cloud-input" type="search" data-discovery-search placeholder="Search packet titles, summaries, bills, or causes" />
        </label>
      </div>
      <p class="civic-note" data-discovery-count></p>
      <div data-discovery-results></div>
    `, 'Discovery')}
  `;

  const resultsHost = shell.root.querySelector<HTMLElement>('[data-discovery-results]');
  const countHost = shell.root.querySelector<HTMLElement>('[data-discovery-count]');
  const searchInput = shell.root.querySelector<HTMLInputElement>('[data-discovery-search]');

  const renderResults = () => {
    const searchTerm = (searchInput?.value || '').trim().toLowerCase();
    const filteredPackets = evidencePackets.filter((evidencePacket) => {
      if (!searchTerm) return true;
      return [
        evidencePacket.title,
        evidencePacket.summary,
        evidencePacket.billCode,
        evidencePacket.causeName,
        evidencePacket.positionStatementTitle
      ].filter(Boolean).some((value) => String(value).toLowerCase().includes(searchTerm));
    });

    if (countHost) {
      countHost.textContent = `${filteredPackets.length} evidence packet${filteredPackets.length === 1 ? '' : 's'} visible`;
    }

    if (resultsHost) {
      resultsHost.innerHTML = renderDiscoveryCards(
        filteredPackets.map((evidencePacket) => ({
          href: evidencePacketHref(evidencePacket.id),
          title: evidencePacket.title,
          summary: evidencePacket.summary || [evidencePacket.billCode, evidencePacket.causeName, evidencePacket.positionStatementTitle].filter(Boolean).join(' · '),
          pills: [
            `${evidencePacket.itemCount} evidence items`,
            `${evidencePacket.actionPacketCount} action packets`,
            evidencePacket.billCode ? `Bill ${evidencePacket.billCode}` : '',
            evidencePacket.causeName ? evidencePacket.causeName : ''
          ].filter(Boolean),
          ctaLabel: 'Open evidence packet'
        })),
        'No evidence packets match that filter.'
      );
    }
  };

  searchInput?.addEventListener('input', renderResults);
  renderResults();
}

async function mountCausePage(shell: CivicPageShell, id: string): Promise<void> {
  setPageShell(shell, 'Loading cause…', 'Cause File', 'Structured civic alignment and action context');
  setDocumentTitle('Cause');
  renderLoading(shell.root, 'Loading cause record…');

  const cause = await fetchCause(id);
  const latestStatements = dedupeLatestStatements(cause.positionStatements);
  const [jurisdictions, supportAggregates, actionPackets] = await Promise.all([
    fetchGeography().catch(() => [] as JurisdictionLookupDto[]),
    fetchSupportAggregates({ targetType: 'cause', targetId: cause.id }),
    fetchActionPackets({ causeId: cause.id })
  ]);

  const relatedBillIds = uniqueValues([
    ...latestStatements.map((statement) => statement.billId),
    ...actionPackets.map((actionPacket) => actionPacket.billId)
  ]).slice(0, 12);
  const relatedOfficialIds = uniqueValues(actionPackets.map((actionPacket) => actionPacket.officialId)).slice(0, 12);
  const [relatedBills, relatedOfficials] = await Promise.all([
    Promise.all(relatedBillIds.map((billId) => fetchBillSafe(billId))),
    Promise.all(relatedOfficialIds.map((officialId) => fetchOfficialSafe(officialId)))
  ]);
  const usableRelatedBills = relatedBills.filter((bill): bill is BillCivicDetailDto => Boolean(bill));
  const usableRelatedOfficials = relatedOfficials.filter((official): official is OfficialDetailDto => Boolean(official));
  const defaultJurisdictionId = chooseDefaultJurisdictionId(usableRelatedBills.map((bill) => bill.jurisdictionId));
  const firstRelatedBill = usableRelatedBills[0] || null;
  const firstRelatedOfficial = usableRelatedOfficials[0] || null;
  const firstActionPacket = actionPackets[0] || null;
  const firstIssue = cause.issues[0] || null;

  setPageShell(shell, cause.name, 'Cause File', cause.summary || 'Mobilize durable civic principles into accountable public action.');
  setDocumentTitle(`${cause.name} | Cause`);

  shell.root.innerHTML = `
    ${renderSummaryBand([
      {
        label: 'What This Means',
        text: summarizeText(cause.summary || cause.description || 'This cause organizes a civic issue into a public object residents can evaluate, support, and act on.')
      },
      {
        label: 'Why It Matters',
        text: `${cause.principles.length} linked principles, ${cause.issues.length} issues, and ${latestStatements.length} current position statements shape this cause.`
      },
      {
        label: 'Next Move',
        text: 'Review the related bills, inspect the latest action packet, then record support if this cause matches your civic view.',
        links: [
          firstRelatedBill ? { href: billHref(firstRelatedBill.id), label: 'Open Bill' } : firstIssue ? { href: issueHref(firstIssue.issueId), label: 'Open Issue' } : { href: 'causes.html', label: 'Browse Causes' },
          firstActionPacket ? { href: actionPacketHref(firstActionPacket.id), label: 'Open Packet' } : firstRelatedOfficial ? { href: officialHref(firstRelatedOfficial.id), label: 'Open Official' } : { href: 'principles.html', label: 'Browse Principles' }
        ]
      }
    ])}
    <div class="civic-layout">
      <div class="civic-main">
        ${renderCard('Cause brief', `
          ${renderMetaPills([
            cause.isActive ? 'Active cause' : 'Inactive cause',
            `${cause.principles.length} principles`,
            `${cause.issues.length} issues`,
            `${latestStatements.length} active statements`
          ])}
          <div class="civic-body-prose">
            ${renderRichText(cause.description || cause.summary || 'No description has been written yet.')}
          </div>
        `, 'Overview')}
        ${renderCard('What residents can do next', renderChecklist([
          'Trace the cause through its linked bills and principle impacts.',
          'Inspect the action packet before contacting an office or sharing a brief.',
          'Record support only after reviewing the active position statements below.'
        ]), 'Actionability')}
        ${renderCard('Active position statements', renderPositionStatements(latestStatements, 'cause'), 'Interpretation')}
        ${renderCard('Related bills', renderEntityLinks(
          usableRelatedBills.map((bill) => ({
            href: billHref(bill.id),
            title: `${bill.billCode} · ${bill.title}`,
            meta: `${bill.principles.length} principle impacts · ${bill.issues.length} linked issues`,
            body: bill.summary || ''
          })),
          'No related bills have been connected yet.'
        ), 'Evidence')}
        ${renderCard('Action packets', renderActionPackets(actionPackets), 'Organize')}
        ${cause.evidencePackets.length ? renderCard('Evidence packets', renderEvidencePackets(cause.evidencePackets, 'No evidence packets attached.'), 'Evidence') : ''}
      </div>
      <aside class="civic-side">
        ${renderCard('Support', `<div data-support-aggregate-block>${renderSupportAggregateBlock(supportAggregates)}</div>`, 'Alignment')}
        ${renderCard('Sign support', `<div id="civic-signature-panel" data-signature-host></div>`, 'Alignment')}
        ${renderCard('Related principles', renderEntityLinks(
          cause.principles.map((principle) => ({
            href: principleHref(principle.principleId),
            title: principle.name,
            body: principle.summary || ''
          })),
          'No principles have been linked yet.'
        ), 'Interpretation')}
        ${renderCard('Related issues', renderEntityLinks(
          cause.issues.map((issue) => ({
            href: issueHref(issue.issueId),
            title: issue.name,
            body: issue.summary || ''
          })),
          'No issues have been linked yet.'
        ), 'Interpretation')}
        ${renderCard('Related officials', renderEntityLinks(
          usableRelatedOfficials.map((official) => ({
            href: officialHref(official.id),
            title: official.fullName,
            meta: [official.party, official.stateCode].filter(Boolean).join(' · '),
            body: official.page?.headline || official.latestScorecard?.summary || ''
          })),
          'No related officials are attached yet.'
        ), 'Accountability')}
        ${renderCard('Cross-links', renderEntityLinks(
          [
            firstIssue ? { href: issueHref(firstIssue.issueId), title: firstIssue.name, body: firstIssue.summary || '' } : null,
            firstRelatedBill ? { href: billHref(firstRelatedBill.id), title: `${firstRelatedBill.billCode} · ${firstRelatedBill.title}`, body: firstRelatedBill.summary || '' } : null,
            firstRelatedOfficial ? { href: officialHref(firstRelatedOfficial.id), title: firstRelatedOfficial.fullName, body: firstRelatedOfficial.page?.headline || '' } : null,
            firstActionPacket ? { href: actionPacketHref(firstActionPacket.id), title: firstActionPacket.title, body: summarizeText(firstActionPacket.body, 120) } : null
          ].filter(Boolean) as Array<{ href: string; title: string; body?: string | null }>,
          'Linked civic records will appear here as this cause gains more evidence and accountability data.'
        ), 'Navigation')}
      </aside>
    </div>
  `;

  const supportHost = shell.root.querySelector<HTMLElement>('[data-support-aggregate-block]');
  const signatureHost = shell.root.querySelector<HTMLElement>('[data-signature-host]');

  if (signatureHost) {
    const signatureTargets: SignatureTargetDefinition[] = [
      {
        targetId: cause.id,
        targetType: 'cause',
        label: cause.name,
        description: cause.summary || cause.description || '',
        defaultJurisdictionId
      },
      ...latestStatements
        .filter((statement) => statement.isSignable)
        .map((statement) => ({
          targetId: statement.id,
          targetType: 'position-statement' as CivicTargetType,
          label: statement.title,
          description: statement.summary,
          defaultJurisdictionId
        }))
    ];

    attachSignaturePanel(shell.root, signatureHost, signatureTargets, jurisdictions, defaultJurisdictionId, async () => {
      if (!supportHost) return;
      const updatedAggregates = await fetchSupportAggregates({ targetType: 'cause', targetId: cause.id });
      supportHost.innerHTML = renderSupportAggregateBlock(updatedAggregates);
    });
  }
}

async function mountPrinciplePage(shell: CivicPageShell, id: string): Promise<void> {
  setPageShell(shell, 'Loading principle…', 'Principle File', 'Durable civic rule with measurable support');
  setDocumentTitle('Principle');
  renderLoading(shell.root, 'Loading principle record…');

  const principle = await fetchPrinciple(id);
  const latestStatements = dedupeLatestStatements(principle.positionStatements);
  const jurisdictions = await fetchGeography().catch(() => [] as JurisdictionLookupDto[]);
  const defaultJurisdictionId = chooseDefaultJurisdictionId(principle.billImpacts.map((billImpact) => billImpact.jurisdictionId));
  const firstBillImpact = principle.billImpacts[0] || null;
  const firstCause = principle.causes[0] || null;
  const firstIssue = principle.issues[0] || null;

  setPageShell(shell, principle.name, 'Principle File', principle.summary || 'Durable standard for interpreting legislation and public accountability.');
  setDocumentTitle(`${principle.name} | Principle`);

  shell.root.innerHTML = `
    ${renderSummaryBand([
      {
        label: 'What This Means',
        text: summarizeText(principle.summary || principle.description || 'This principle is a durable civic rule used to evaluate legislation and accountability records.')
      },
      {
        label: 'Nonpartisan Frame',
        text: 'Principles are presented here as standards for interpretation, not party branding or campaign language.'
      },
      {
        label: 'Next Move',
        text: 'Use this principle to enter a related cause, inspect bill impacts, or support the principle directly.',
        links: [
          firstCause ? { href: causeHref(firstCause.id), label: 'Open Cause' } : firstIssue ? { href: issueHref(firstIssue.issueId), label: 'Open Issue' } : { href: 'causes.html', label: 'Browse Causes' },
          firstBillImpact ? { href: billHref(firstBillImpact.billId), label: 'Open Bill' } : firstIssue ? { href: issueHref(firstIssue.issueId), label: 'Open Issue' } : { href: 'principles.html', label: 'Browse Principles' }
        ]
      }
    ])}
    <div class="civic-layout">
      <div class="civic-main">
        ${renderCard('Principle brief', `
          ${renderMetaPills([
            principle.isSignable ? 'Signable' : 'Read only',
            `${principle.causes.length} causes`,
            `${principle.issues.length} issues`,
            `${principle.billImpacts.length} bill impacts`
          ])}
          <div class="civic-body-prose">
            ${renderRichText(principle.description || principle.summary || 'No description has been written yet.')}
          </div>
        `, 'Overview')}
        ${renderCard('How to read this principle', renderChecklist([
          'Start with the principle itself before moving into any cause or bill impact.',
          'Treat linked causes as applications of this rule, not the rule itself.',
          'Use bill impacts below to see where this principle is already being tested.'
        ]), 'Reading Guide')}
        ${renderCard('Related bill impacts', renderBillImpacts(principle.billImpacts), 'Evidence')}
        ${renderCard('Active position statements', renderPositionStatements(latestStatements, 'principle'), 'Interpretation')}
      </div>
      <aside class="civic-side">
        ${renderCard('Support', `<div data-support-aggregate-block>${renderSupportAggregateBlock(principle.supportAggregates)}</div>`, 'Alignment')}
        ${renderCard('Sign support', `<div id="civic-signature-panel" data-signature-host></div>`, 'Alignment')}
        ${renderCard('Related causes', renderEntityLinks(
          principle.causes.map((cause) => ({
            href: causeHref(cause.id),
            title: cause.name,
            body: cause.summary || ''
          })),
          'No causes have been linked yet.'
        ), 'Interpretation')}
        ${renderCard('Related issues', renderEntityLinks(
          principle.issues.map((issue) => ({
            href: issueHref(issue.issueId),
            title: issue.name,
            body: issue.summary || ''
          })),
          'No issues have been linked yet.'
        ), 'Interpretation')}
        ${renderCard('Cross-links', renderEntityLinks(
          [
            firstIssue ? { href: issueHref(firstIssue.issueId), title: firstIssue.name, body: firstIssue.summary || '' } : null,
            firstCause ? { href: causeHref(firstCause.id), title: firstCause.name, body: firstCause.summary || '' } : null,
            firstBillImpact ? { href: billHref(firstBillImpact.billId), title: `${firstBillImpact.billCode} · ${firstBillImpact.title}`, body: firstBillImpact.summary || '' } : null
          ].filter(Boolean) as Array<{ href: string; title: string; body?: string | null }>,
          'Related causes and bills will appear here as the principle is applied across the civic graph.'
        ), 'Navigation')}
      </aside>
    </div>
  `;

  const supportHost = shell.root.querySelector<HTMLElement>('[data-support-aggregate-block]');
  const signatureHost = shell.root.querySelector<HTMLElement>('[data-signature-host]');

  if (signatureHost) {
    const signatureTargets: SignatureTargetDefinition[] = [
      {
        targetId: principle.id,
        targetType: 'principle',
        label: principle.name,
        description: principle.summary || principle.description || '',
        defaultJurisdictionId
      },
      ...latestStatements
        .filter((statement) => statement.isSignable)
        .map((statement) => ({
          targetId: statement.id,
          targetType: 'position-statement' as CivicTargetType,
          label: statement.title,
          description: statement.summary,
          defaultJurisdictionId
        }))
    ];

    attachSignaturePanel(shell.root, signatureHost, signatureTargets, jurisdictions, defaultJurisdictionId, async () => {
      if (!supportHost) return;
      const updatedAggregates = await fetchSupportAggregates({ targetType: 'principle', targetId: principle.id });
      supportHost.innerHTML = renderSupportAggregateBlock(updatedAggregates);
    });
  }
}

async function mountBillPage(shell: CivicPageShell, id: string): Promise<void> {
  setPageShell(shell, 'Loading bill…', 'Bill Civic Detail', 'Linked issues, principles, evidence, and position statements');
  setDocumentTitle('Bill');
  renderLoading(shell.root, 'Loading bill civic detail…');

  const [bill, jurisdictions] = await Promise.all([
    fetchBill(id),
    fetchGeography().catch(() => [] as JurisdictionLookupDto[])
  ]);
  const actionPackets = await fetchActionPackets({ billId: bill.id }).catch(() => [] as ActionPacketDto[]);
  const latestStatements = dedupeLatestStatements(bill.positionStatements);
  const signableStatements = latestStatements.filter((statement) => statement.isSignable);
  const firstPrinciple = bill.principles[0] || null;
  const firstActionPacket = actionPackets[0] || null;
  const firstCauseId = latestStatements.find((statement) => statement.causeId)?.causeId || null;
  const firstIssue = bill.issues[0] || null;

  setPageShell(shell, `${bill.billCode} · ${bill.title}`, 'Bill Civic Detail', bill.summary || 'Legislative artifact connected to civic interpretation and evidence.');
  setDocumentTitle(`${bill.billCode} | Bill`);

  shell.root.innerHTML = `
    ${renderSummaryBand([
      {
        label: 'What This Bill Means',
        text: summarizeText(bill.summary || 'This bill has not been summarized yet, but the linked issues and principle impacts below show its current civic reading.')
      },
      {
        label: 'Civic Reading',
        text: `${bill.issues.length} linked issues, ${bill.principles.length} principle impacts, and ${latestStatements.length} active position statements shape this bill record.`
      },
      {
        label: 'Next Move',
        text: 'Read the principle impacts first, then inspect signable position statements or the latest action packet.',
        links: [
          firstPrinciple ? { href: principleHref(firstPrinciple.principleId), label: 'Open Principle' } : { href: 'principles.html', label: 'Browse Principles' },
          firstActionPacket ? { href: actionPacketHref(firstActionPacket.id), label: 'Open Packet' } : firstCauseId ? { href: causeHref(firstCauseId), label: 'Open Cause' } : { href: 'causes.html', label: 'Browse Causes' }
        ]
      }
    ])}
    <div class="civic-layout">
      <div class="civic-main">
        ${renderCard('Bill brief', `
          ${renderMetaPills([
            bill.billCode,
            `${bill.issues.length} issues`,
            `${bill.principles.length} principle impacts`,
            `${latestStatements.length} active statements`
          ])}
          <div class="civic-body-prose">
            ${renderRichText(bill.summary || 'No summary has been attached yet.')}
          </div>
        `, 'Evidence')}
        ${renderCard('How to use this bill page', renderChecklist([
          'Start with the issue and principle links to understand the current civic interpretation.',
          'Use position statements to see how the bill is being framed publicly.',
          'Open related action packets before contacting an office or sharing a briefing.'
        ]), 'Reading Guide')}
        ${renderCard('Principle impacts', renderEntityLinks(
          bill.principles.map((principleImpact) => ({
            href: principleHref(principleImpact.principleId),
            title: principleImpact.principleName,
            meta: `${humanizeSlug(principleImpact.impactDirection)} · ${formatConfidence(principleImpact.confidence)}`,
            body: principleImpact.rationale || ''
          })),
          'No principle impacts have been linked yet.'
        ), 'Interpretation')}
        ${renderCard('Position statements', renderPositionStatements(latestStatements, 'bill'), 'Interpretation')}
        ${renderCard('Action packets', renderActionPackets(actionPackets), 'Organize')}
        ${renderCard('Evidence packets', renderEvidencePackets(bill.evidencePackets, 'No evidence packets attached.'), 'Evidence')}
      </div>
      <aside class="civic-side">
        ${renderCard('Linked issues', renderEntityLinks(
          bill.issues.map((issue) => ({
            href: issueHref(issue.issueId),
            title: issue.issueName,
            meta: `${humanizeSlug(issue.linkType)} · ${formatConfidence(issue.confidence)}`,
            body: issue.rationale || ''
          })),
          'No issue links have been added yet.'
        ), 'Interpretation')}
        ${renderCard('Signature flow', signableStatements.length ? '<div id="civic-signature-panel" data-signature-host></div>' : renderEmptyState('No signable position statements are attached yet.'), 'Alignment')}
        ${renderCard('Cross-links', renderEntityLinks(
          [
            firstIssue ? { href: issueHref(firstIssue.issueId), title: firstIssue.issueName, body: firstIssue.rationale || '' } : null,
            firstCauseId ? { href: causeHref(firstCauseId), title: 'Related cause', body: 'Move from the bill into the broader civic object built around it.' } : null,
            firstPrinciple ? { href: principleHref(firstPrinciple.principleId), title: firstPrinciple.principleName, body: firstPrinciple.rationale || '' } : null,
            firstActionPacket ? { href: actionPacketHref(firstActionPacket.id), title: firstActionPacket.title, body: summarizeText(firstActionPacket.body, 120) } : null
          ].filter(Boolean) as Array<{ href: string; title: string; body?: string | null }>,
          'Related causes, principles, and action packets will appear here when linked.'
        ), 'Navigation')}
      </aside>
    </div>
  `;

  const signatureHost = shell.root.querySelector<HTMLElement>('[data-signature-host]');
  if (signatureHost && signableStatements.length) {
    const signatureTargets: SignatureTargetDefinition[] = signableStatements.map((statement) => ({
      targetId: statement.positionStatementId,
      targetType: 'position-statement',
      label: statement.title,
      description: statement.summary,
      defaultJurisdictionId: bill.jurisdictionId
    }));

    attachSignaturePanel(shell.root, signatureHost, signatureTargets, jurisdictions, bill.jurisdictionId);
  }
}

async function mountOfficialPage(shell: CivicPageShell, id: string): Promise<void> {
  setPageShell(shell, 'Loading official…', 'Official File', 'Accountability record for terms, questions, contradictions, and scorecard');
  setDocumentTitle('Official');
  renderLoading(shell.root, 'Loading official record…');

  const official = await fetchOfficial(id);
  const actionPackets = await fetchActionPackets({ officialId: official.id }).catch(() => [] as ActionPacketDto[]);
  const currentTerm = official.terms.find((term) => term.isCurrent) || official.terms[0] || null;
  const unansweredQuestionsCount = official.publicQuestions.filter((question) => question.responses.length === 0).length;
  const firstActionPacket = actionPackets[0] || null;

  setPageShell(
    shell,
    official.fullName,
    'Official File',
    official.page?.headline || 'Public accountability record for this official.'
  );
  setDocumentTitle(`${official.fullName} | Official`);

  shell.root.innerHTML = `
    ${renderSummaryBand([
      {
        label: 'What This Record Shows',
        text: summarizeText(official.page?.summary || official.page?.headline || 'This file collects the public accountability record for the official, including questions, contradictions, terms, and scorecards.')
      },
      {
        label: 'Accountability Status',
        text: `${official.contradictions.length} contradiction records, ${unansweredQuestionsCount} unanswered public questions, and ${official.latestScorecard ? `a score of ${official.latestScorecard.score.toFixed(1)}` : 'no current scorecard yet'}.`
      },
      {
        label: 'Next Move',
        text: 'Inspect unanswered questions and contradiction records first, then review linked action packets and position records.',
        links: [
          firstActionPacket ? { href: actionPacketHref(firstActionPacket.id), label: 'Open Packet' } : { href: 'officials.html', label: 'Browse Officials' }
        ]
      }
    ])}
    <div class="civic-layout">
      <div class="civic-main">
        ${renderCard('Official brief', `
          ${renderMetaPills([
            official.party,
            official.stateCode,
            currentTerm ? currentTerm.officeName : null
          ])}
          <div class="civic-body-prose">
            ${renderRichText(official.page?.summary || official.page?.headline || 'No official summary is attached yet.')}
          </div>
        `, 'Accountability')}
        ${renderCard('Public questions and responses', renderQuestions(official.publicQuestions), 'Confront')}
        ${renderCard('Contradiction records', renderContradictions(official.contradictions), 'Accountability')}
        ${renderCard('Office and term history', renderOfficialTerms(official.terms), 'Geography')}
      </div>
      <aside class="civic-side">
        ${official.latestScorecard ? renderCard('Current scorecard', `
          <div class="civic-scorecard">
            <strong>${official.latestScorecard.score.toFixed(1)}</strong>
            <span>Version ${official.latestScorecard.versionNumber}</span>
          </div>
          <p>${escapeHtml(official.latestScorecard.summary)}</p>
          <p class="civic-note">${escapeHtml(formatDateTime(official.latestScorecard.createdUtc))}</p>
        `, 'Measure') : renderCard('Current scorecard', renderEmptyState('No scorecard has been published yet.'), 'Measure')}
        ${renderCard('Action packets', renderActionPackets(actionPackets), 'Organize')}
        ${renderCard('Position records', renderPositionRecords(official.positionRecords), 'Interpretation')}
      </aside>
    </div>
  `;
}

async function mountEvidencePacketPage(shell: CivicPageShell, id: string): Promise<void> {
  setPageShell(shell, 'Loading evidence packet…', 'Evidence Packet', 'Reusable evidence bundle linked to civic interpretation and action.');
  setDocumentTitle('Evidence Packet');
  renderLoading(shell.root, 'Loading evidence packet…');

  const evidencePacket = await fetchEvidencePacket(id);
  const firstActionPacket = evidencePacket.actionPackets[0] || null;
  const firstOfficial = evidencePacket.officials[0] || null;

  setPageShell(
    shell,
    evidencePacket.title,
    'Evidence Packet',
    evidencePacket.summary || 'Reusable evidence bundle linked to civic interpretation and action.'
  );
  setDocumentTitle(`${evidencePacket.title} | Evidence Packet`);

  shell.root.innerHTML = `
    ${renderSummaryBand([
      {
        label: 'What This Means',
        text: summarizeText(evidencePacket.summary || 'This evidence packet collects reusable source material that can support position statements and action packets.')
      },
      {
        label: 'Packet Scope',
        text: `${evidencePacket.items.length} evidence items, ${evidencePacket.actionPackets.length} linked action packets, and ${evidencePacket.officials.length} linked officials are attached to this packet.`
      },
      {
        label: 'Next Move',
        text: 'Read the included evidence items first, then move into the linked bill, cause, official, or action packet records.',
        links: [
          evidencePacket.bill ? { href: billHref(evidencePacket.bill.id), label: 'Open Bill' } : evidencePacket.cause ? { href: causeHref(evidencePacket.cause.id), label: 'Open Cause' } : { href: 'index.html#policy-cloud-hero', label: 'Back to Explore' },
          firstActionPacket ? { href: actionPacketHref(firstActionPacket.id), label: 'Open Packet' } : firstOfficial ? { href: officialHref(firstOfficial.id), label: 'Open Official' } : { href: 'causes.html', label: 'Browse Causes' }
        ]
      }
    ])}
    <div class="civic-layout">
      <div class="civic-main">
        ${renderCard('Evidence packet brief', `
          ${renderMetaPills([
            evidencePacket.bill ? 'Bill linked' : null,
            evidencePacket.cause ? 'Cause linked' : null,
            evidencePacket.positionStatement ? 'Position statement linked' : null,
            `${evidencePacket.items.length} evidence items`
          ])}
          <div class="civic-body-prose">
            ${renderRichText(evidencePacket.summary || 'No evidence packet summary has been attached yet.')}
          </div>
        `, 'Evidence')}
        ${renderCard('Included evidence items', renderEvidencePacketItems(evidencePacket.items), 'Repository')}
      </div>
      <aside class="civic-side">
        ${renderCard('Linked records', renderEntityLinks(
          [
            evidencePacket.bill ? { href: billHref(evidencePacket.bill.id), title: `${evidencePacket.bill.billCode} · ${evidencePacket.bill.title}`, body: evidencePacket.bill.summary || '' } : null,
            evidencePacket.cause ? { href: causeHref(evidencePacket.cause.id), title: evidencePacket.cause.name, body: evidencePacket.cause.summary || '' } : null,
            firstOfficial ? { href: officialHref(firstOfficial.id), title: firstOfficial.fullName, body: [firstOfficial.party, firstOfficial.officeName].filter(Boolean).join(' · ') } : null
          ].filter(Boolean) as Array<{ href: string; title: string; body?: string | null }>,
          'No linked bill, cause, or official records are attached.'
        ), 'References')}
        ${renderCard('Action packets', renderActionPackets(evidencePacket.actionPackets), 'Organize')}
        ${evidencePacket.positionStatement
          ? renderCard('Linked position statement', renderPositionStatements([evidencePacket.positionStatement], 'evidence-packet'), 'Interpretation')
          : renderCard('Linked position statement', renderEmptyState('No position statement is attached to this evidence packet.'), 'Interpretation')}
      </aside>
    </div>
  `;
}

async function mountSourceDocumentPage(shell: CivicPageShell, id: string): Promise<void> {
  setPageShell(shell, 'Loading source document…', 'Source Document', 'Public source material attached to the civic evidence graph.');
  setDocumentTitle('Source Document');
  renderLoading(shell.root, 'Loading source document…');

  const sourceDocument = await fetchSourceDocument(id);
  const firstEvidencePacket = sourceDocument.evidencePackets[0] || null;
  const firstCause = sourceDocument.causes[0] || null;
  const firstOfficial = sourceDocument.officials[0] || null;
  const firstActionPacket = sourceDocument.actionPackets[0] || null;

  setPageShell(shell, sourceDocument.title, 'Source Document', `${sourceDocument.sourceName} source material.`);
  setDocumentTitle(`${sourceDocument.title} | Source Document`);

  shell.root.innerHTML = `
    ${renderSummaryBand([
      {
        label: 'What This Means',
        text: `This source document came from ${sourceDocument.sourceName} and now anchors public evidence in the civic graph.`
      },
      {
        label: 'Retrieval Status',
        text: `Retrieved ${formatDateTime(sourceDocument.retrievedUtc)} as ${humanizeSlug(sourceDocument.documentType)}${sourceDocument.mimeType ? ` · ${sourceDocument.mimeType}` : ''}.`
      },
      {
        label: 'Next Move',
        text: 'Read the source material first, then move into linked evidence packets, bills, causes, or action packets.',
        links: [
          firstEvidencePacket ? { href: evidencePacketHref(firstEvidencePacket.id), label: 'Open Evidence Packet' } : { href: evidencePacketsHref(), label: 'Browse Evidence' },
          sourceDocument.bill ? { href: billHref(sourceDocument.bill.id), label: 'Open Bill' } : firstActionPacket ? { href: actionPacketHref(firstActionPacket.id), label: 'Open Packet' } : { href: 'index.html#policy-cloud-hero', label: 'Back to Explore' }
        ]
      }
    ])}
    <div class="civic-layout">
      <div class="civic-main">
        ${renderCard('Source brief', `
          ${renderMetaPills([
            sourceDocument.sourceName,
            humanizeSlug(sourceDocument.documentType),
            sourceDocument.mimeType,
            humanizeSlug(sourceDocument.sourceEntityType)
          ])}
          <div class="civic-body-prose">
            <p>Source entity: ${escapeHtml(sourceDocument.sourceEntityId)}</p>
          </div>
        `, 'Authority')}
        ${renderCard('Readable content', renderDocumentContent(sourceDocument.readableContent, 'No readable source content is available yet.'), 'Content')}
      </div>
      <aside class="civic-side">
        ${renderCard('Retrieval metadata', `
          ${renderMetaPills([
            `Retrieved ${formatDateTime(sourceDocument.retrievedUtc)}`,
            `Created ${formatDateTime(sourceDocument.createdUtc)}`,
            `Updated ${formatDateTime(sourceDocument.updatedUtc)}`
          ])}
          ${sourceDocument.sourceUrl ? `<div class="civic-reference-row"><a class="civic-reference-link" href="${escapeHtml(sourceDocument.sourceUrl)}" target="_blank" rel="noopener">Open source authority</a></div>` : renderEmptyState('No source URL is attached.')}
        `, 'Metadata')}
        ${renderCard('Linked civic records', renderEntityLinks(
          [
            sourceDocument.bill ? { href: billHref(sourceDocument.bill.id), title: `${sourceDocument.bill.billCode} · ${sourceDocument.bill.title}`, body: sourceDocument.bill.summary || '' } : null,
            firstCause ? { href: causeHref(firstCause.id), title: firstCause.name, body: firstCause.summary || '' } : null,
            firstOfficial ? { href: officialHref(firstOfficial.id), title: firstOfficial.fullName, body: [firstOfficial.party, firstOfficial.officeName].filter(Boolean).join(' · ') } : null
          ].filter(Boolean) as Array<{ href: string; title: string; body?: string | null }>,
          'No linked civic records are attached yet.'
        ), 'Context')}
        ${renderCard('Evidence packets', renderEntityLinks(
          sourceDocument.evidencePackets.map((evidencePacket) => ({
            href: evidencePacketHref(evidencePacket.id),
            title: evidencePacket.title,
            body: evidencePacket.summary || ''
          })),
          'No evidence packets reference this source document yet.'
        ), 'Repository')}
        ${renderCard('Action packets', renderActionPackets(sourceDocument.actionPackets), 'Organize')}
      </aside>
    </div>
  `;
}

async function mountRecordDocumentPage(shell: CivicPageShell, id: string): Promise<void> {
  setPageShell(shell, 'Loading record document…', 'Record Document', 'Public record derived from civic evidence or accountability events.');
  setDocumentTitle('Record Document');
  renderLoading(shell.root, 'Loading record document…');

  const recordDocument = await fetchRecordDocument(id);
  const firstCause = recordDocument.causes[0] || null;
  const firstOfficial = recordDocument.officials[0] || null;
  const firstEvidencePacket = recordDocument.evidencePackets[0] || null;
  const firstActionPacket = recordDocument.actionPackets[0] || null;

  setPageShell(shell, recordDocument.title, 'Record Document', `${humanizeSlug(recordDocument.recordType)} record linked to the civic graph.`);
  setDocumentTitle(`${recordDocument.title} | Record Document`);

  shell.root.innerHTML = `
    ${renderSummaryBand([
      {
        label: 'What This Means',
        text: `This ${humanizeSlug(recordDocument.recordType)} record captures a civic event or accountability artifact that can be reused in evidence packets and action work.`
      },
      {
        label: 'Record Timing',
        text: `Created ${formatDateTime(recordDocument.createdUtc)} and last updated ${formatDateTime(recordDocument.updatedUtc)}.`
      },
      {
        label: 'Next Move',
        text: 'Read the record content, then move into its parent source document, linked bill, or related evidence packet.',
        links: [
          recordDocument.parentSourceDocument ? { href: sourceDocumentHref(recordDocument.parentSourceDocument.id), label: 'Open Parent Source' } : firstEvidencePacket ? { href: evidencePacketHref(firstEvidencePacket.id), label: 'Open Evidence Packet' } : { href: evidencePacketsHref(), label: 'Browse Evidence' },
          recordDocument.bill ? { href: billHref(recordDocument.bill.id), label: 'Open Bill' } : firstActionPacket ? { href: actionPacketHref(firstActionPacket.id), label: 'Open Packet' } : { href: 'index.html#policy-cloud-hero', label: 'Back to Explore' }
        ]
      }
    ])}
    <div class="civic-layout">
      <div class="civic-main">
        ${renderCard('Record brief', `
          ${renderMetaPills([
            humanizeSlug(recordDocument.recordType),
            `Created ${formatDateTime(recordDocument.createdUtc)}`,
            `Updated ${formatDateTime(recordDocument.updatedUtc)}`
          ])}
          <div class="civic-body-prose">
            <p>${escapeHtml(recordDocument.title)}</p>
          </div>
        `, 'Record')}
        ${renderCard('Record content', renderDocumentContent(recordDocument.contentText, 'No public record text is available yet.'), 'Content')}
      </div>
      <aside class="civic-side">
        ${renderCard('Parent source', recordDocument.parentSourceDocument
          ? renderEntityLinks(
            [{
              href: sourceDocumentHref(recordDocument.parentSourceDocument.id),
              title: recordDocument.parentSourceDocument.title,
              body: recordDocument.parentSourceDocument.sourceName
            }],
            'No parent source document is attached.')
          : renderEmptyState('No parent source document is attached.'), 'Repository')}
        ${renderCard('Linked civic records', renderEntityLinks(
          [
            recordDocument.bill ? { href: billHref(recordDocument.bill.id), title: `${recordDocument.bill.billCode} · ${recordDocument.bill.title}`, body: recordDocument.bill.summary || '' } : null,
            firstCause ? { href: causeHref(firstCause.id), title: firstCause.name, body: firstCause.summary || '' } : null,
            firstOfficial ? { href: officialHref(firstOfficial.id), title: firstOfficial.fullName, body: [firstOfficial.party, firstOfficial.officeName].filter(Boolean).join(' · ') } : null
          ].filter(Boolean) as Array<{ href: string; title: string; body?: string | null }>,
          'No linked bill, cause, or official records are attached.'
        ), 'Context')}
        ${renderCard('Evidence packets', renderEntityLinks(
          recordDocument.evidencePackets.map((evidencePacket) => ({
            href: evidencePacketHref(evidencePacket.id),
            title: evidencePacket.title,
            body: evidencePacket.summary || ''
          })),
          'No evidence packets reference this record document yet.'
        ), 'Repository')}
        ${renderCard('Action packets', renderActionPackets(recordDocument.actionPackets), 'Organize')}
      </aside>
    </div>
  `;
}

async function mountExplanationPage(shell: CivicPageShell, id: string): Promise<void> {
  setPageShell(shell, 'Loading explanation…', 'Explanation', 'Public interpretation record linked to a civic position statement.');
  setDocumentTitle('Explanation');
  renderLoading(shell.root, 'Loading explanation…');

  const explanation = await fetchExplanation(id);
  const firstEvidencePacket = explanation.evidencePackets[0] || null;
  const firstActionPacket = explanation.actionPackets[0] || null;

  setPageShell(shell, explanation.title, 'Explanation', explanation.positionStatement.summary);
  setDocumentTitle(`${explanation.title} | Explanation`);

  shell.root.innerHTML = `
    ${renderSummaryBand([
      {
        label: 'What This Means',
        text: 'This explanation records the interpretive reasoning attached to a position statement in the civic graph.'
      },
      {
        label: 'Version Status',
        text: `${explanation.isCurrent ? 'Current explanation' : 'Historical explanation'} · version ${explanation.versionNumber} · published ${formatDateTime(explanation.createdUtc)}.`
      },
      {
        label: 'Next Move',
        text: 'Read the explanation body, then inspect the linked position statement, evidence packet, bill, or cause context.',
        links: [
          firstEvidencePacket ? { href: evidencePacketHref(firstEvidencePacket.id), label: 'Open Evidence Packet' } : { href: evidencePacketsHref(), label: 'Browse Evidence' },
          explanation.bill ? { href: billHref(explanation.bill.id), label: 'Open Bill' } : explanation.cause ? { href: causeHref(explanation.cause.id), label: 'Open Cause' } : { href: 'index.html#policy-cloud-hero', label: 'Back to Explore' }
        ]
      }
    ])}
    <div class="civic-layout">
      <div class="civic-main">
        ${renderCard('Explanation body', renderDocumentContent(explanation.body, 'No explanation text is available yet.'), 'Interpretation')}
      </div>
      <aside class="civic-side">
        ${renderCard('Version detail', `
          ${renderMetaPills([
            `Version ${explanation.versionNumber}`,
            explanation.isCurrent ? 'Current' : 'Historical',
            `Published ${formatDateTime(explanation.createdUtc)}`
          ])}
        `, 'Status')}
        ${renderCard('Linked position statement', renderPositionStatements([explanation.positionStatement], 'explanation'), 'Context')}
        ${renderCard('Linked civic records', renderEntityLinks(
          [
            explanation.bill ? { href: billHref(explanation.bill.id), title: `${explanation.bill.billCode} · ${explanation.bill.title}`, body: explanation.bill.summary || '' } : null,
            explanation.cause ? { href: causeHref(explanation.cause.id), title: explanation.cause.name, body: explanation.cause.summary || '' } : null,
            firstActionPacket ? { href: actionPacketHref(firstActionPacket.id), title: firstActionPacket.title, body: formatDateTime(firstActionPacket.createdUtc) } : null
          ].filter(Boolean) as Array<{ href: string; title: string; body?: string | null }>,
          'No linked bill, cause, or action packet records are attached.'
        ), 'Context')}
        ${renderCard('Evidence packets', renderEntityLinks(
          explanation.evidencePackets.map((evidencePacket) => ({
            href: evidencePacketHref(evidencePacket.id),
            title: evidencePacket.title,
            body: evidencePacket.summary || ''
          })),
          'No evidence packets reference this explanation yet.'
        ), 'Repository')}
        ${renderCard('Action packets', renderActionPackets(explanation.actionPackets), 'Organize')}
      </aside>
    </div>
  `;
}

async function mountRiskAssessmentPage(shell: CivicPageShell, id: string): Promise<void> {
  setPageShell(shell, 'Loading risk assessment…', 'Risk Assessment', 'Public risk analysis linked to a civic position statement.');
  setDocumentTitle('Risk Assessment');
  renderLoading(shell.root, 'Loading risk assessment…');

  const riskAssessment = await fetchRiskAssessment(id);
  const firstEvidencePacket = riskAssessment.evidencePackets[0] || null;
  const firstActionPacket = riskAssessment.actionPackets[0] || null;

  setPageShell(shell, riskAssessment.title, 'Risk Assessment', `${humanizeSlug(riskAssessment.severity)} severity civic risk record.`);
  setDocumentTitle(`${riskAssessment.title} | Risk Assessment`);

  shell.root.innerHTML = `
    ${renderSummaryBand([
      {
        label: 'What This Means',
        text: 'This risk assessment records the harms or accountability exposure tied to the linked position statement and civic context.'
      },
      {
        label: 'Risk Profile',
        text: `${humanizeSlug(riskAssessment.severity)} severity${riskAssessment.confidenceText ? ` · ${riskAssessment.confidenceText}` : ''} · version ${riskAssessment.versionNumber}.`
      },
      {
        label: 'Next Move',
        text: 'Read the risk narrative, then inspect the linked bill, cause, evidence packet, or action packet before acting on it.',
        links: [
          firstEvidencePacket ? { href: evidencePacketHref(firstEvidencePacket.id), label: 'Open Evidence Packet' } : { href: evidencePacketsHref(), label: 'Browse Evidence' },
          riskAssessment.bill ? { href: billHref(riskAssessment.bill.id), label: 'Open Bill' } : riskAssessment.cause ? { href: causeHref(riskAssessment.cause.id), label: 'Open Cause' } : { href: 'index.html#policy-cloud-hero', label: 'Back to Explore' }
        ]
      }
    ])}
    <div class="civic-layout">
      <div class="civic-main">
        ${renderCard('Risk narrative', renderDocumentContent(riskAssessment.narrative, 'No risk narrative is available yet.'), 'Assessment')}
      </div>
      <aside class="civic-side">
        ${renderCard('Risk detail', `
          ${renderMetaPills([
            humanizeSlug(riskAssessment.severity),
            riskAssessment.confidenceText || 'Confidence not recorded',
            riskAssessment.isCurrent ? 'Current' : 'Historical',
            `Version ${riskAssessment.versionNumber}`
          ])}
          <p class="civic-note">${escapeHtml(formatDateTime(riskAssessment.createdUtc))}</p>
        `, 'Status')}
        ${renderCard('Linked position statement', renderPositionStatements([riskAssessment.positionStatement], 'risk-assessment'), 'Context')}
        ${renderCard('Linked civic records', renderEntityLinks(
          [
            riskAssessment.bill ? { href: billHref(riskAssessment.bill.id), title: `${riskAssessment.bill.billCode} · ${riskAssessment.bill.title}`, body: riskAssessment.bill.summary || '' } : null,
            riskAssessment.cause ? { href: causeHref(riskAssessment.cause.id), title: riskAssessment.cause.name, body: riskAssessment.cause.summary || '' } : null,
            firstActionPacket ? { href: actionPacketHref(firstActionPacket.id), title: firstActionPacket.title, body: formatDateTime(firstActionPacket.createdUtc) } : null
          ].filter(Boolean) as Array<{ href: string; title: string; body?: string | null }>,
          'No linked bill, cause, or action packet records are attached.'
        ), 'Context')}
        ${renderCard('Evidence packets', renderEntityLinks(
          riskAssessment.evidencePackets.map((evidencePacket) => ({
            href: evidencePacketHref(evidencePacket.id),
            title: evidencePacket.title,
            body: evidencePacket.summary || ''
          })),
          'No evidence packets reference this risk assessment yet.'
        ), 'Repository')}
        ${renderCard('Action packets', renderActionPackets(riskAssessment.actionPackets), 'Organize')}
      </aside>
    </div>
  `;
}

async function mountActionPacketPage(shell: CivicPageShell, id: string): Promise<void> {
  setPageShell(shell, 'Loading action packet…', 'Action Packet', 'Generated action artifact tied to evidence and accountability');
  setDocumentTitle('Action Packet');
  renderLoading(shell.root, 'Loading action packet…');

  const actionPacket = await fetchActionPacket(id);
  const [cause, bill, official, evidencePacket] = await Promise.all([
    actionPacket.causeId ? fetchCause(actionPacket.causeId).catch(() => null) : Promise.resolve(null),
    actionPacket.billId ? fetchBill(actionPacket.billId).catch(() => null) : Promise.resolve(null),
    actionPacket.officialId ? fetchOfficial(actionPacket.officialId).catch(() => null) : Promise.resolve(null),
    actionPacket.evidencePacketId ? fetchEvidencePacket(actionPacket.evidencePacketId).catch(() => null) : Promise.resolve(null)
  ]);

  setPageShell(
    shell,
    actionPacket.title,
    'Action Packet',
    summarizeText(actionPacket.body, 180) || 'Generated civic action artifact.'
  );
  setDocumentTitle(`${actionPacket.title} | Action Packet`);

  shell.root.innerHTML = `
    ${renderSummaryBand([
      {
        label: 'What This Packet Does',
        text: summarizeText(actionPacket.body, 180) || 'This packet packages evidence and civic framing into an action-ready artifact.'
      },
      {
        label: 'Linked Records',
        text: `${cause ? 'Cause linked.' : 'No cause linked.'} ${bill ? 'Bill linked.' : 'No bill linked.'} ${official ? 'Official linked.' : 'No official linked.'} ${evidencePacket ? 'Evidence packet linked.' : 'No evidence packet linked.'}`
      },
      {
        label: 'Next Move',
        text: 'Read the packet body, then move into the linked bill, cause, or official record to verify the framing.',
        links: [
          cause ? { href: causeHref(cause.id), label: 'Open Cause' } : bill ? { href: billHref(bill.id), label: 'Open Bill' } : { href: 'index.html#policy-cloud-hero', label: 'Back to Explore' },
          evidencePacket ? { href: evidencePacketHref(evidencePacket.id), label: 'Open Evidence' } : official ? { href: officialHref(official.id), label: 'Open Official' } : { href: 'causes.html', label: 'Browse Causes' }
        ]
      }
    ])}
    <div class="civic-layout">
      <div class="civic-main">
        ${renderCard('Packet body', renderActionPacketBody(actionPacket.body), 'Organize')}
      </div>
      <aside class="civic-side">
        ${renderCard('Packet detail', `
          ${renderMetaPills([
            humanizeSlug(actionPacket.templateKey || 'packet'),
            humanizeSlug(actionPacket.status),
            `Version ${actionPacket.versionNumber}`
          ])}
          <p class="civic-note">${escapeHtml(formatDateTime(actionPacket.createdUtc))}</p>
        `, 'Summary')}
        ${renderCard('Linked references', renderEntityLinks(
          [
            cause ? { href: causeHref(cause.id), title: cause.name, body: cause.summary || '' } : null,
            bill ? { href: billHref(bill.id), title: `${bill.billCode} · ${bill.title}`, body: bill.summary || '' } : null,
            official ? { href: officialHref(official.id), title: official.fullName, body: official.page?.headline || '' } : null,
            evidencePacket ? { href: evidencePacketHref(evidencePacket.id), title: evidencePacket.title, body: evidencePacket.summary || '' } : null
          ].filter(Boolean) as Array<{ href: string; title: string; body?: string | null }>,
          'No linked cause, bill, official, or evidence records are attached.'
        ), 'References')}
        ${renderCard('Evidence reference', evidencePacket ? renderEntityLinks(
          [{
            href: evidencePacketHref(evidencePacket.id),
            title: evidencePacket.title,
            meta: `${evidencePacket.items.length} evidence items`,
            body: evidencePacket.summary || ''
          }],
          'No evidence packet attached.'
        ) : renderEmptyState('No evidence packet detail is attached.'), 'Evidence')}
      </aside>
    </div>
  `;
}

async function mountCivicPage(): Promise<void> {
  const root = document.querySelector<HTMLElement>('[data-civic-page]');
  if (!root) return;

  const pageKind = root.dataset.civicPage as CivicPageKind | undefined;
  const id = readQueryParam('id');
  const shell: CivicPageShell = {
    root,
    title: document.querySelector<HTMLElement>('[data-civic-page-title]'),
    kicker: document.querySelector<HTMLElement>('[data-civic-page-kicker]'),
    subhead: document.querySelector<HTMLElement>('[data-civic-page-subhead]')
  };

  if (!pageKind) return;

  if (!id && pageKind !== 'causes' && pageKind !== 'principles' && pageKind !== 'issues' && pageKind !== 'officials' && pageKind !== 'evidence-packets') {
    setPageShell(shell, 'Missing civic record', 'Civic File', 'A record id is required to render this page.');
    setDocumentTitle('Missing civic record');
    renderFailure(root, 'No civic record id was provided in the URL.');
    return;
  }

  try {
    if (pageKind === 'causes') {
      await mountCausesPage(shell);
      return;
    }

    if (pageKind === 'principles') {
      await mountPrinciplesPage(shell);
      return;
    }

    if (pageKind === 'issues') {
      await mountIssuesPage(shell);
      return;
    }

    if (pageKind === 'officials') {
      await mountOfficialsPage(shell);
      return;
    }

    if (pageKind === 'evidence-packets') {
      await mountEvidencePacketsPage(shell);
      return;
    }

    if (pageKind === 'cause') {
      await mountCausePage(shell, id);
      return;
    }

    if (pageKind === 'principle') {
      await mountPrinciplePage(shell, id);
      return;
    }

    if (pageKind === 'bill') {
      await mountBillPage(shell, id);
      return;
    }

    if (pageKind === 'official') {
      await mountOfficialPage(shell, id);
      return;
    }

    if (pageKind === 'issue') {
      await mountIssuePage(shell, id);
      return;
    }

    if (pageKind === 'evidence-packet') {
      await mountEvidencePacketPage(shell, id);
      return;
    }

    if (pageKind === 'source-document') {
      await mountSourceDocumentPage(shell, id);
      return;
    }

    if (pageKind === 'record-document') {
      await mountRecordDocumentPage(shell, id);
      return;
    }

    if (pageKind === 'explanation') {
      await mountExplanationPage(shell, id);
      return;
    }

    if (pageKind === 'risk-assessment') {
      await mountRiskAssessmentPage(shell, id);
      return;
    }

    if (pageKind === 'action-packet') {
      await mountActionPacketPage(shell, id);
      return;
    }
  } catch (error) {
    renderFailure(root, error instanceof Error ? error.message : 'Unable to load this civic record.');
  }
}

document.addEventListener('DOMContentLoaded', () => {
  mountCivicPage().catch(() => {});
});
